import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { SSAOPass } from 'three/addons/postprocessing/SSAOPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { OutlinePass } from 'three/addons/postprocessing/OutlinePass.js';
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';
import { FXAAShader } from 'three/addons/shaders/FXAAShader.js';
import { parseAwmh, sourceToThree, SOURCE_TO_METRES } from './awmesh.js';
import { calibratedLayerBox, fitDistanceForBox } from './camera-fit.js';
import { adaptCs2Frame, createResourceDemoFrame, createScenarioDemoFrame } from './cs2-adapter.js';
import { findMapEntry, normalizeMapId, validateRegistry } from './map-registry.js';
import {
  resolveRenderProfile,
  resolveRenderQuality,
  resolveSurfaceAppearance,
  shouldShowMapEdges,
} from './map-materials.js';
import { selectLayerForHeight, validateFloorIndex } from './layer-selection.js';
import { createPowOperatorSkinnedMesh, loadPowOperatorGeometry } from './operator-model.js';
import { applyTriangleSurfaceGroups, parseTriangleSurfaces } from './triangle-surfaces.js';

const $ = (id) => document.getElementById(id);
const runtimeDebugEnabled = new URLSearchParams(window.location.search).has('debug');
const ui = {
  threeView: $('threeView'),
  twoView: $('twoView'),
  statusDot: $('statusDot'),
  statusText: $('statusText'),
  latency: $('latency'),
  connectionMode: $('connectionMode'),
  officialFields: $('officialFields'),
  directFields: $('directFields'),
  roomId: $('roomId'),
  roomPassword: $('roomPassword'),
  wsUrl: $('wsUrl'),
  connect: $('connectButton'),
  demo: $('demoButton'),
  entryLanding: $('entryLanding'),
  entryTitle: $('entryTitle'),
  entryDetail: $('entryDetail'),
  entryJoin: $('entryJoin'),
  entryDemo: $('entryDemo'),
  mapSelect: $('mapSelect'),
  demoScenario: $('demoScenario'),
  demoPlayPause: $('demoPlayPause'),
  demoTimeline: $('demoTimeline'),
  demoTime: $('demoTime'),
  demoEventRail: $('demoEventRail'),
  demoEventDetail: $('demoEventDetail'),
  demoState: $('demoState'),
  demoSpeed: $('demoSpeed'),
  demoRestart: $('demoRestart'),
  demoDirector: $('demoDirector'),
  fit: $('fitButton'),
  renderStyle: $('renderStyle'),
  mapTextureBlend: $('mapTextureBlend'),
  mapTextureValue: $('mapTextureValue'),
  showBuyzones: $('showBuyzones'),
  showSpawns: $('showSpawns'),
  showCallouts: $('showCallouts'),
  showLineups: $('showLineups'),
  showMapEntities: $('showMapEntities'),
  renderQuality: $('renderQuality'),
  layerMode: $('layerMode'),
  layerSelect: $('layerSelect'),
  layerState: $('layerState'),
  roofCut: $('roofCut'),
  heightCut: $('heightCut'),
  heightValue: $('heightValue'),
  showDead: $('showDead'),
  showPlayerLabels: $('showPlayerLabels'),
  showTrails: $('showTrails'),
  playerList: $('playerList'),
  entityCount: $('entityCount'),
  calibrationState: $('calibrationState'),
  calibrationDetail: $('calibrationDetail'),
  resourceState: $('resourceState'),
  resourceDetail: $('resourceDetail'),
  geometryLoading: $('geometryLoading'),
  geometryLoadingTitle: $('geometryLoadingTitle'),
  geometryLoadingDetail: $('geometryLoadingDetail'),
  frameState: $('frameState'),
  viewHint: $('viewHint'),
  combatFeed: $('combatFeed'),
  focusHud: $('focusHud'),
  focusExit: $('focusExit'),
  focusName: $('focusName'),
  focusAction: $('focusAction'),
  focusDetail: $('focusDetail'),
  roundHud: $('roundHud'),
  roundCtAlive: $('roundCtAlive'),
  roundTAlive: $('roundTAlive'),
  roundCtPips: $('roundCtPips'),
  roundTPips: $('roundTPips'),
  roundObjective: $('roundObjective'),
  roundResult: $('roundResult'),
  mapName: $('mapName'),
  mapCode: $('mapCode'),
  activeLayer: $('activeLayer'),
  brandSubtitle: $('brandSubtitle'),
};

const scene = new THREE.Scene();
scene.background = new THREE.Color(0x09111a);
scene.fog = new THREE.FogExp2(0x09111a, 0.0065);
const camera = new THREE.PerspectiveCamera(45, 1, 0.05, 1000);
camera.up.set(0, 1, 0);
const renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: 'high-performance' });
renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.12;
renderer.localClippingEnabled = true;
renderer.shadowMap.enabled = false;
ui.threeView.append(renderer.domElement);
const controls = new OrbitControls(camera, renderer.domElement);
controls.enableDamping = true;
controls.dampingFactor = 0.08;
controls.screenSpacePanning = true;
controls.maxPolarAngle = Math.PI * 0.49;
controls.minDistance = 3;
controls.maxDistance = 260;

const ambient = new THREE.AmbientLight(0xffffff, 0.32);
scene.add(ambient);
const hemisphere = new THREE.HemisphereLight(0x9bdcff, 0x121a24, 1.18);
scene.add(hemisphere);
const sun = new THREE.DirectionalLight(0xe7f7ff, 2.15);
sun.position.set(18, 42, 24);
scene.add(sun);
const fillLight = new THREE.DirectionalLight(0x5bb8ff, 0.55);
fillLight.position.set(-28, 18, -22);
scene.add(fillLight);

const environmentGenerator = new THREE.PMREMGenerator(renderer);
const environmentScene = new RoomEnvironment(renderer);
const environmentTarget = environmentGenerator.fromScene(environmentScene, 0.04);
scene.environment = environmentTarget.texture;
environmentScene.dispose();
environmentGenerator.dispose();

const composer = new EffectComposer(renderer);
const renderPass = new RenderPass(scene, camera);
const ssaoPass = new SSAOPass(scene, camera, 1, 1, 8);
ssaoPass.normalMaterial.side = THREE.DoubleSide;
ssaoPass.normalMaterial.flatShading = true;
ssaoPass.kernelRadius = 7;
ssaoPass.minDistance = 0.0025;
ssaoPass.maxDistance = 0.11;
const outputPass = new OutputPass();
const operatorOutlinePass = new OutlinePass(new THREE.Vector2(1, 1), scene, camera);
operatorOutlinePass.edgeStrength = 2.35;
operatorOutlinePass.edgeGlow = 0.16;
operatorOutlinePass.edgeThickness = 0.86;
operatorOutlinePass.pulsePeriod = 0;
operatorOutlinePass.visibleEdgeColor.set(0x79d8ff);
operatorOutlinePass.hiddenEdgeColor.set(0x173548);
const compatibleFxaaShader = {
  ...FXAAShader,
  fragmentShader: FXAAShader.fragmentShader.replaceAll('-100.0', '-16.0'),
};
const fxaaPass = new ShaderPass(compatibleFxaaShader);
composer.addPass(renderPass);
composer.addPass(ssaoPass);
composer.addPass(operatorOutlinePass);
composer.addPass(outputPass);
composer.addPass(fxaaPass);
const grid = new THREE.GridHelper(240, 48, 0x18334a, 0x122638);
grid.position.y = -8;
grid.material.transparent = true;
grid.material.opacity = 0.33;
scene.add(grid);

const entityRoot = new THREE.Group();
scene.add(entityRoot);
const playerEntityRoot = new THREE.Group();
playerEntityRoot.name = 'persistent-players';
const transientEntityRoot = new THREE.Group();
transientEntityRoot.name = 'transient-entities';
const projectileEntityRoot = new THREE.Group();
projectileEntityRoot.name = 'persistent-projectile-effects';
entityRoot.add(playerEntityRoot, projectileEntityRoot, transientEntityRoot);
const annotationRoot = new THREE.Group();
annotationRoot.name = 'tactical-annotations';
scene.add(annotationRoot);
const clippingPlane = new THREE.Plane(new THREE.Vector3(0, -1, 0), 2);
const mapContextFloorPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
let mapMesh = null;
let mapEdges = null;
let mapSurfaceData = null;
let mapRegistry = null;
let mapManifest = null;
let calibration = null;
let floorIndex = null;
let radarImage = null;
let radarTexture = null;
let buyzoneImage = null;
let buyzoneTexture = null;
let radarProjection = null;
let tacticalAnnotations = null;
let mapLiveValidation = null;
let mapResourceBundle = null;
let mapDemoScenarios = null;
let mapGrenadeKnowledge = null;
let mapDynamicEntities = null;
let currentMapBase = null;
let geometryLoadPromise = null;
let currentMapId = null;
let activeLayer = null;
let loadingMapId = null;
let mapLoadPromise = null;
let pendingFrame = null;
let currentFrame = null;
let liveSocket = null;
let liveWsUrl = '';
let liveReconnectTimer = null;
let liveReconnectAttempt = 0;
let demoMode = false;
let demoMapId = 'de_mirage';
let currentView = new URLSearchParams(location.search).get('view') === '2d' ? '2d' : '3d';
let roomAllows3d = true;
let lastDemoUpdate = 0;
let lastFrameAt = performance.now();
let liveProviderConnected = false;
let lastLiveHealthState = '';
let lastReferenceHeight = null;
let mapTriangleCount = 0;
let demoElapsedMs = 0;
let demoPaused = true;
let demoPlaybackRate = 1;
let demoDirectorEnabled = false;
let lastDirectedEventId = '';
let directorTransitionActive = false;
let lastAnimationTime = 0;
let focusedPlayerId = null;
const playerTrails = new Map();
const playerMarkerById = new Map();
const projectileEffectById = new Map();
const rosterRowById = new Map();
const demoEventButtonById = new Map();
const focusedCameraTarget = new THREE.Vector3();
const focusedCameraOffset = new THREE.Vector3();
const directorCameraTarget = new THREE.Vector3();
const directorCameraPosition = new THREE.Vector3();
let lastDemoEventRailSignature = '';
let lastCombatFeedSignature = '';
let lastCombatBurstCount = 0;
controls.addEventListener('start', () => { directorTransitionActive = false; });

const EDGE_TRIANGLE_BUDGET = 450000;
const TRAIL_POINT_LIMIT = 28;
const markerGeometry = Object.freeze({
  body: new THREE.CylinderGeometry(0.20, 0.24, 1.35, 10),
  head: new THREE.SphereGeometry(0.21, 12, 8),
  direction: new THREE.ConeGeometry(0.16, 0.55, 8),
  point: new THREE.SphereGeometry(0.2, 12, 8),
  health: new THREE.TorusGeometry(0.31, 0.035, 6, 18),
});
const combatGeometry = Object.freeze({
  beam: new THREE.CylinderGeometry(0.026, 0.018, 1, 6, 1, true),
  impact: new THREE.RingGeometry(0.12, 0.24, 20),
});
const combatMaterials = Object.freeze({
  damage: new THREE.MeshBasicMaterial({ color: 0xffb05b, transparent: true, opacity: 0.92, depthTest: false }),
  kill: new THREE.MeshBasicMaterial({ color: 0xff405c, transparent: true, opacity: 0.98, depthTest: false }),
});
const projectileGeometry = Object.freeze({
  fallback: new THREE.IcosahedronGeometry(0.11, 1),
  trail: new THREE.CylinderGeometry(0.018, 0.055, 1, 6, 1, true),
  cue: new THREE.RingGeometry(0.14, 0.22, 24),
  smoke: new THREE.IcosahedronGeometry(0.42, 2),
  flash: new THREE.SphereGeometry(0.23, 16, 12),
  shockwave: new THREE.TorusGeometry(0.72, 0.035, 8, 48),
  spark: new THREE.SphereGeometry(0.045, 6, 4),
  area: new THREE.CircleGeometry(1, 48),
  areaRing: new THREE.RingGeometry(0.88, 1, 48),
});
const PLAYER_ACTION_LABELS = Object.freeze({
  idle: '出生等待',
  walk: '移动',
  run: '快速推进',
  hold: '持枪架点',
  throw: '投掷道具',
  plant: '下包',
  fire: '交火射击',
  reload: '更换弹匣',
  scoped: '开镜架枪',
  flashed: '被闪光影响',
  hit: '被命中',
  death: '阵亡倒地',
  defuse: '拆除 C4',
  victory: '回合获胜',
});
const DEMO_OBJECTIVE_LABELS = Object.freeze({
  carried: 'C4 携带中',
  planted: 'C4 已安放',
  defusing: 'CT 正在拆包',
  defused: 'C4 已拆除',
  exploded: 'C4 已爆炸',
  inactive: '区域控制',
});
const PLAYER_ROLE_LABELS = Object.freeze({
  entry: '突破位',
  trade: '补枪位',
  lurk: '控图位',
  anchor: '主防位',
  support: '协防位',
  rotate: '转点位',
  retake: '回防位',
  sniper: '狙击位',
});
const markerMaterials = new Map();
const operatorMaterials = new Map();
const operatorGeometries = new Map();
const officialWeaponTemplates = new Map();
const operatorEquipmentMaterials = new Map();
const operatorEquipmentGeometry = Object.freeze({
  box: new THREE.BoxGeometry(1, 1, 1),
  barrel: new THREE.CylinderGeometry(1, 1, 1, 8),
  grenade: new THREE.SphereGeometry(0.065, 10, 8),
});
let operatorGeometryState = 'loading';
let officialWeaponState = 'loading';
let lastRosterSignature = '';

function createRadarTexture(image) {
  const texture = new THREE.Texture(image);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = true;
  texture.anisotropy = Math.min(16, renderer.capabilities.getMaxAnisotropy());
  texture.needsUpdate = true;
  return texture;
}

function createSemanticTexture(image) {
  if (!image) return null;
  const texture = new THREE.Texture(image);
  texture.colorSpace = THREE.NoColorSpace;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = true;
  texture.anisotropy = Math.min(16, renderer.capabilities.getMaxAnisotropy());
  texture.needsUpdate = true;
  return texture;
}

function createRadarProjection(calibrationData, image, texture) {
  const matrix = calibrationData?.transform?.worldToImage;
  const width = Number(calibrationData?.image?.width || image?.naturalWidth || image?.width || 1);
  const height = Number(calibrationData?.image?.height || image?.naturalHeight || image?.height || 1);
  if (!texture || !Array.isArray(matrix) || matrix.length !== 2 || width <= 0 || height <= 0) return null;
  return {
    texture,
    row0: new THREE.Vector3(matrix[0][0] / width, matrix[0][1] / width, matrix[0][2] / width),
    row1: new THREE.Vector3(matrix[1][0] / height, matrix[1][1] / height, matrix[1][2] / height),
    texelSize: new THREE.Vector2(1 / width, 1 / height),
  };
}

function projectionForLayer(layer) {
  if (!calibration || !radarImage || !radarTexture) return null;
  const projection = createRadarProjection({
    ...calibration,
    transform: layer?.transform || calibration.transform,
  }, radarImage, radarTexture);
  if (projection) projection.buyzoneTexture = buyzoneTexture;
  return projection;
}

function normalizedLayerUvBounds(layer, projection = radarProjection) {
  const bounds = layer?.imageBounds;
  const width = projection?.texelSize?.x ? 1 / projection.texelSize.x : 0;
  const height = projection?.texelSize?.y ? 1 / projection.texelSize.y : 0;
  if (!bounds || width <= 0 || height <= 0) return new THREE.Vector4(0, 0, 1, 1);
  return new THREE.Vector4(
    THREE.MathUtils.clamp(Number(bounds.xMin) / width, 0, 1),
    THREE.MathUtils.clamp(1 - Number(bounds.yMax) / height, 0, 1),
    THREE.MathUtils.clamp(Number(bounds.xMax) / width, 0, 1),
    THREE.MathUtils.clamp(1 - Number(bounds.yMin) / height, 0, 1),
  );
}

function shouldCropToLayer(layer) {
  return ui.roofCut.checked && availableLayers().length > 1 && layer?.id !== 'default';
}

function applyLayerProjection(layer) {
  const projection = projectionForLayer(layer);
  if (!projection) return;
  radarProjection = projection;
  const style = resolveRenderProfile(currentMapId, ui.renderStyle?.value);
  forEachMapMaterial((material) => {
    const uniforms = material.userData?.radarUniforms;
    if (!uniforms) return;
    const affinity = Number(material.userData?.radarSurfaceAppearance?.mapAffinity ?? 1);
    uniforms.radarMapTexture.value = projection.texture;
    uniforms.radarBuyzoneTexture.value = projection.buyzoneTexture;
    uniforms.radarMapRow0.value.copy(projection.row0);
    uniforms.radarMapRow1.value.copy(projection.row1);
    uniforms.radarMapTexelSize.value.copy(projection.texelSize);
    uniforms.radarLayerUvBounds.value.copy(normalizedLayerUvBounds(layer, projection));
    uniforms.radarLayerCrop.value = shouldCropToLayer(layer) ? 1 : 0;
    uniforms.radarMapBlend.value = selectedMapTextureStrength(style) * affinity;
    uniforms.radarBuyzoneBlend.value = projection.buyzoneTexture && ui.showBuyzones?.checked
      ? 0.72 * affinity : 0;
  });
}

function selectedMapTextureStrength(style) {
  const userStrength = THREE.MathUtils.clamp(Number(ui.mapTextureBlend?.value ?? 72) / 100, 0, 1);
  return style.mapTextureBlend * userStrength;
}

function tintedSurfaceColor(base, surface, styleId) {
  const tintScale = styleId === 'realistic' ? 1 : styleId === 'gray' ? 0.08 : 0.34;
  return new THREE.Color(base).lerp(
    new THREE.Color(surface.tint),
    THREE.MathUtils.clamp(surface.tintStrength * tintScale, 0, 1),
  );
}

function createRadarMaterial(
  bounds,
  styleId = 'realistic',
  mapId = currentMapId,
  projection = radarProjection,
  surfaceName = 'default',
) {
  const style = resolveRenderProfile(mapId, styleId);
  const surface = resolveSurfaceAppearance(surfaceName);
  const quality = resolveRenderQuality(ui.renderQuality?.value);
  const zLow = bounds?.min?.z ?? -300;
  const range = Math.max(64, (bounds?.max?.z ?? 220) - zLow);
  const uniforms = {
    radarZLow: { value: zLow },
    radarZInvRange: { value: 1 / range },
    radarGroundLow: { value: tintedSurfaceColor(style.groundLow, surface, styleId) },
    radarGroundHigh: { value: tintedSurfaceColor(style.groundHigh, surface, styleId) },
    radarWallLow: { value: tintedSurfaceColor(style.wallLow, surface, styleId) },
    radarWallHigh: { value: tintedSurfaceColor(style.wallHigh, surface, styleId) },
    radarRoofLow: { value: tintedSurfaceColor(style.roofLow, surface, styleId) },
    radarRoofHigh: { value: tintedSurfaceColor(style.roofHigh, surface, styleId) },
    radarRoad: { value: tintedSurfaceColor(style.road, surface, styleId) },
    radarAccent: { value: tintedSurfaceColor(style.accent, surface, styleId) },
    radarMapTexture: { value: projection?.texture || null },
    radarBuyzoneTexture: { value: projection?.buyzoneTexture || null },
    radarMapRow0: { value: projection?.row0 || new THREE.Vector3() },
    radarMapRow1: { value: projection?.row1 || new THREE.Vector3() },
    radarMapTexelSize: { value: projection?.texelSize || new THREE.Vector2(1 / 2048, 1 / 2048) },
    radarMapSharpness: { value: quality.sharpness },
    radarMapBlend: { value: projection ? selectedMapTextureStrength(style) * surface.mapAffinity : 0 },
    radarBuyzoneBlend: {
      value: projection?.buyzoneTexture && ui.showBuyzones?.checked ? 0.72 * surface.mapAffinity : 0,
    },
    radarBuyzoneColor: { value: new THREE.Color(style.buyzoneColor) },
    radarEdge: { value: new THREE.Color(style.edge) },
    radarColorStrength: { value: style.colorStrength },
    radarDetailStrength: { value: style.detailStrength * surface.detailStrength },
    radarDetailScale: { value: surface.detailScale },
    radarSemanticStrength: { value: style.semanticStrength },
    radarContourStrength: { value: style.contourStrength },
    radarWallStrength: { value: style.wallStrength },
    radarLayerFloor: { value: Number(activeLayer?.zMin ?? zLow) },
    radarLayerContextStrength: { value: availableLayers().length > 1 ? 0.58 : 0.18 },
    radarLayerUvBounds: { value: normalizedLayerUvBounds(activeLayer, projection) },
    radarLayerCrop: { value: shouldCropToLayer(activeLayer) ? 1 : 0 },
  };
  const material = new THREE.MeshStandardMaterial({
    color: 0xffffff,
    roughness: styleId === 'realistic' && surface.roughness !== null ? surface.roughness : style.roughness,
    metalness: styleId === 'realistic' && surface.metalness !== null ? surface.metalness : style.metalness,
    envMapIntensity: style.envMapIntensity,
    side: THREE.DoubleSide,
    flatShading: true,
    clippingPlanes: ui.roofCut?.checked ? [clippingPlane, mapContextFloorPlane] : [],
  });
  material.name = `map-surface-${surface.family}-${surfaceName}`;
  material.visible = surface.visible;
  material.userData.radarSurfaceAppearance = surface;
  material.userData.radarUniforms = uniforms;
  material.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, uniforms);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vRadarPosition;\nvarying vec3 vRadarNormal;\nvarying float vRadarHeight;\nvarying float vRadarUpness;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvRadarPosition = position;\nvRadarNormal = normalize(normal);\nvRadarHeight = position.z;\nvRadarUpness = abs(normal.z);');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>
varying vec3 vRadarPosition;
varying vec3 vRadarNormal;
varying float vRadarHeight;
varying float vRadarUpness;
uniform float radarZLow;
uniform float radarZInvRange;
uniform vec3 radarGroundLow;
uniform vec3 radarGroundHigh;
uniform vec3 radarWallLow;
uniform vec3 radarWallHigh;
uniform vec3 radarRoofLow;
uniform vec3 radarRoofHigh;
uniform vec3 radarRoad;
uniform vec3 radarAccent;
uniform sampler2D radarMapTexture;
uniform sampler2D radarBuyzoneTexture;
uniform vec3 radarMapRow0;
uniform vec3 radarMapRow1;
uniform vec2 radarMapTexelSize;
uniform float radarMapSharpness;
uniform float radarMapBlend;
uniform float radarBuyzoneBlend;
uniform vec3 radarBuyzoneColor;
uniform vec3 radarEdge;
uniform float radarColorStrength;
uniform float radarDetailStrength;
uniform float radarDetailScale;
uniform float radarSemanticStrength;
uniform float radarContourStrength;
uniform float radarWallStrength;
uniform float radarLayerFloor;
uniform float radarLayerContextStrength;
uniform vec4 radarLayerUvBounds;
uniform float radarLayerCrop;

float radarHash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float radarNoise21(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  return mix(mix(radarHash21(i), radarHash21(i + vec2(1.0, 0.0)), f.x),
    mix(radarHash21(i + vec2(0.0, 1.0)), radarHash21(i + vec2(1.0, 1.0)), f.x), f.y);
}

float radarBrick(vec2 p) {
  vec2 cell = p / vec2(96.0, 42.0);
  cell.x += mod(floor(cell.y), 2.0) * 0.5;
  vec2 edgeDistance = min(fract(cell), 1.0 - fract(cell));
  return 1.0 - smoothstep(0.035, 0.085, min(edgeDistance.x, edgeDistance.y));
}`)
      .replace('#include <normal_fragment_maps>', `#include <normal_fragment_maps>
float radarHeight01 = clamp((vRadarHeight - radarZLow) * radarZInvRange, 0.0, 1.0);
float radarHorizontal = smoothstep(0.38, 0.82, vRadarUpness);
float radarWallness = 1.0 - smoothstep(0.18, 0.70, vRadarUpness);
vec3 radarNormalWeight = pow(abs(vRadarNormal), vec3(3.0));
radarNormalWeight /= max(dot(radarNormalWeight, vec3(1.0)), 0.0001);
float radarTriNoise = radarNoise21(vRadarPosition.yz * 0.018 * radarDetailScale) * radarNormalWeight.x
  + radarNoise21(vRadarPosition.xz * 0.018 * radarDetailScale) * radarNormalWeight.y
  + radarNoise21(vRadarPosition.xy * 0.018 * radarDetailScale) * radarNormalWeight.z;
float radarLargeNoise = radarNoise21(vRadarPosition.xy * 0.0045 * radarDetailScale);
vec3 radarGround = mix(radarGroundLow, radarGroundHigh, radarHeight01);
vec3 radarWall = mix(radarWallLow, radarWallHigh, radarHeight01);
vec3 radarRoof = mix(radarRoofLow, radarRoofHigh, radarHeight01);
float radarRoofMask = radarHorizontal * smoothstep(0.56, 0.84, radarHeight01);
float radarPavedMask = radarHorizontal * (1.0 - radarRoofMask) * smoothstep(0.58, 0.78, radarLargeNoise);
float radarBrickLines = max(radarBrick(vRadarPosition.xz), radarBrick(vRadarPosition.yz)) * radarWallness;
radarGround = mix(radarGround, radarRoad, radarPavedMask * 0.42 * radarSemanticStrength);
radarWall *= mix(0.88, 1.08, radarTriNoise);
radarWall = mix(radarWall, radarAccent, radarBrickLines * 0.07 * radarSemanticStrength);
radarRoof *= mix(0.82, 1.12, radarLargeNoise);
vec3 radarSurface = mix(radarWall, radarGround, radarHorizontal);
radarSurface = mix(radarSurface, radarRoof, radarRoofMask * radarSemanticStrength);
radarSurface *= mix(0.87, 1.13, radarTriNoise * radarDetailStrength);
vec3 radarMapPoint = vec3(vRadarPosition.xy, 1.0);
vec2 radarMapUv = vec2(dot(radarMapRow0, radarMapPoint), 1.0 - dot(radarMapRow1, radarMapPoint));
float radarLayerInside = step(radarLayerUvBounds.x, radarMapUv.x)
  * step(radarMapUv.x, radarLayerUvBounds.z)
  * step(radarLayerUvBounds.y, radarMapUv.y)
  * step(radarMapUv.y, radarLayerUvBounds.w);
if (radarLayerCrop > 0.5 && radarLayerInside < 0.5) discard;
float radarMapInside = step(0.0, radarMapUv.x) * step(radarMapUv.x, 1.0)
  * step(0.0, radarMapUv.y) * step(radarMapUv.y, 1.0);
vec2 radarMapUvClamped = clamp(radarMapUv, 0.0, 1.0);
vec4 radarMapCenter = texture2D(radarMapTexture, radarMapUvClamped);
vec3 radarMapNeighbour = (
  texture2D(radarMapTexture, clamp(radarMapUvClamped + vec2(radarMapTexelSize.x, 0.0), 0.0, 1.0)).rgb
  + texture2D(radarMapTexture, clamp(radarMapUvClamped - vec2(radarMapTexelSize.x, 0.0), 0.0, 1.0)).rgb
  + texture2D(radarMapTexture, clamp(radarMapUvClamped + vec2(0.0, radarMapTexelSize.y), 0.0, 1.0)).rgb
  + texture2D(radarMapTexture, clamp(radarMapUvClamped - vec2(0.0, radarMapTexelSize.y), 0.0, 1.0)).rgb
) * 0.25;
vec4 radarMapSample = vec4(
  clamp(radarMapCenter.rgb + (radarMapCenter.rgb - radarMapNeighbour) * radarMapSharpness, 0.0, 1.0),
  radarMapCenter.a
);
vec4 radarBuyzoneSample = texture2D(radarBuyzoneTexture, clamp(radarMapUv, 0.0, 1.0));
float radarMapLuma = dot(radarMapSample.rgb, vec3(0.2126, 0.7152, 0.0722));
float radarMapCoverage = radarMapInside * max(radarMapSample.a, smoothstep(0.025, 0.12, radarMapLuma));
float radarMapLine = smoothstep(0.62, 0.94, radarMapLuma);
float radarMapSite = smoothstep(0.05, 0.28, radarMapSample.r - max(radarMapSample.g, radarMapSample.b));
vec3 radarMapTint = mix(radarRoad, radarGroundHigh, 0.34 + radarMapLuma * 0.26);
radarMapTint = mix(radarMapTint, radarAccent, radarMapLine * 0.42);
radarMapTint = mix(radarMapTint, radarRoofHigh, radarMapSite * 0.48);
float radarMapSurfaceMask = radarHorizontal * (1.0 - radarRoofMask * 0.62) * radarMapCoverage;
radarSurface = mix(radarSurface, radarMapTint, radarMapBlend * radarMapSurfaceMask);
float radarBuyzoneMask = radarMapInside * radarBuyzoneSample.a
  * smoothstep(0.28, 0.82, radarBuyzoneSample.g) * radarHorizontal * (1.0 - radarRoofMask * 0.72);
radarSurface = mix(radarSurface, radarBuyzoneColor, radarBuyzoneMask * radarBuyzoneBlend);
float radarBelowLayer = 1.0 - smoothstep(
  radarLayerFloor - 32.0,
  radarLayerFloor + 8.0,
  vRadarHeight
);
float radarContextLuma = dot(radarSurface, vec3(0.2126, 0.7152, 0.0722));
vec3 radarContextSurface = mix(vec3(radarContextLuma * 0.58), radarSurface, 0.26);
radarSurface = mix(
  radarSurface,
  radarContextSurface,
  radarBelowLayer * radarLayerContextStrength
);
float radarContourCoord = (vRadarHeight - radarZLow) / 64.0;
float radarContourPhase = abs(fract(radarContourCoord + 0.5) - 0.5);
float radarContourWidth = clamp(fwidth(radarContourCoord) * 1.4, 0.002, 0.14);
float radarContour = 1.0 - smoothstep(radarContourWidth, radarContourWidth * 2.3, radarContourPhase);
diffuseColor.rgb = mix(diffuseColor.rgb, radarSurface, radarColorStrength);
totalEmissiveRadiance += radarEdge
  * (radarContour * radarContourStrength + radarWallness * radarWallStrength)
  * (1.0 - radarBelowLayer * radarLayerContextStrength * 0.72);`)
      .replace('#include <roughnessmap_fragment>', `#include <roughnessmap_fragment>
float radarPbrNoise = radarNoise21(vRadarPosition.xy * 0.025)
  * 0.45 + radarNoise21(vRadarPosition.yz * 0.041) * 0.30
  + radarNoise21(vRadarPosition.xz * 0.063) * 0.25;
roughnessFactor = clamp(roughnessFactor * mix(0.88, 1.12, radarPbrNoise), 0.24, 1.0);`)
      .replace('#include <metalnessmap_fragment>', `#include <metalnessmap_fragment>
float radarPbrSide = 1.0 - smoothstep(0.26, 0.72, abs(vRadarNormal.z));
metalnessFactor = clamp(metalnessFactor + radarPbrSide * radarPbrNoise * 0.035, 0.0, 0.16);`);
  };
  material.customProgramCacheKey = () => `cs2-radar-render-v7-${mapId}-${styleId}-${projection ? 'map' : 'plain'}`;
  return material;
}

function createRadarMaterials(bounds, styleId = 'realistic', mapId = currentMapId) {
  if (!mapSurfaceData?.surfaceNames?.length) {
    return createRadarMaterial(bounds, styleId, mapId);
  }
  return mapSurfaceData.surfaceNames.map((surfaceName) => (
    createRadarMaterial(bounds, styleId, mapId, radarProjection, surfaceName)
  ));
}

function forEachMapMaterial(callback) {
  if (!mapMesh?.material) return;
  const materials = Array.isArray(mapMesh.material) ? mapMesh.material : [mapMesh.material];
  materials.forEach(callback);
}

function disposeMapMaterial(material) {
  const materials = Array.isArray(material) ? material : [material];
  materials.forEach((item) => item?.dispose?.());
}

function applyRenderStyle() {
  const styleId = ui.renderStyle?.value || 'realistic';
  const style = resolveRenderProfile(currentMapId, styleId);
  scene.background.setHex(style.background);
  scene.fog.color.setHex(style.background);
  scene.fog.density = style.fogDensity;
  grid.visible = styleId === 'structure';
  ambient.intensity = style.ambientIntensity;
  hemisphere.color.setHex(style.hemisphereSky);
  hemisphere.groundColor.setHex(style.hemisphereGround);
  hemisphere.intensity = style.hemisphereIntensity;
  sun.color.setHex(style.sunColor);
  sun.intensity = style.sunIntensity;
  fillLight.color.setHex(style.fillColor);
  fillLight.intensity = style.fillIntensity;
  renderer.toneMappingExposure = style.exposure
    + resolveRenderQuality(ui.renderQuality?.value).exposure - resolveRenderQuality('balanced').exposure;
  if (mapMesh) {
    const previous = mapMesh.material;
    mapMesh.material = createRadarMaterials(mapMesh.geometry.boundingBox, styleId, currentMapId);
    disposeMapMaterial(previous);
  }
  if (mapEdges) {
    mapEdges.material.color.setHex(style.edge);
    mapEdges.material.opacity = style.outlineOpacity;
    mapEdges.visible = shouldShowMapEdges(styleId, ui.renderQuality?.value) && !shouldCropToLayer(activeLayer);
  }
}

function applyRenderQuality() {
  const profile = resolveRenderQuality(ui.renderQuality?.value);
  const balanced = resolveRenderQuality('balanced');
  const style = resolveRenderProfile(currentMapId, ui.renderStyle?.value);
  const deviceRatio = window.devicePixelRatio || 1;
  const deviceFloor = ui.renderQuality?.value === 'performance'
    ? Math.min(deviceRatio, 1.25)
    : Math.min(deviceRatio, 2);
  renderer.setPixelRatio(Math.min(2.5, Math.max(deviceFloor, profile.pixelRatio)));
  composer.setPixelRatio(renderer.getPixelRatio());
  renderer.toneMappingExposure = style.exposure + (profile.exposure - balanced.exposure);
  ssaoPass.enabled = profile.ssao;
  ssaoPass.kernelRadius = profile.kernelRadius;
  fxaaPass.enabled = profile.fxaa;
  operatorOutlinePass.enabled = ui.renderQuality?.value !== 'performance';
  if (mapEdges) {
    mapEdges.visible = shouldShowMapEdges(ui.renderStyle?.value, ui.renderQuality?.value)
      && !shouldCropToLayer(activeLayer);
  }
  forEachMapMaterial((material) => {
    const uniforms = material.userData?.radarUniforms;
    if (uniforms?.radarMapSharpness) uniforms.radarMapSharpness.value = profile.sharpness;
  });
  resize();
}

function setStatus(kind, message, badge) {
  ui.statusText.textContent = message;
  ui.latency.textContent = badge;
  const colors = { ok: '#35e08d', wait: '#f4b94f', error: '#ff4e64' };
  ui.statusDot.style.background = colors[kind] || colors.wait;
  ui.statusDot.style.boxShadow = `0 0 12px ${colors[kind] || colors.wait}`;
}

function markerMaterial(color, kind = 'standard') {
  const key = `${kind}:${color}`;
  if (markerMaterials.has(key)) return markerMaterials.get(key);
  const options = {
    color,
    depthTest: false,
    transparent: true,
    opacity: kind === 'trail' ? 0.72 : 1,
  };
  const material = kind === 'basic'
    ? new THREE.MeshBasicMaterial(options)
    : new THREE.MeshStandardMaterial({
      ...options,
      emissive: color,
      emissiveIntensity: 0.34,
      roughness: 0.6,
    });
  markerMaterials.set(key, material);
  return material;
}

function operatorMaterial(team, local = false) {
  const key = `body:${team}:${local ? 'local' : 'remote'}`;
  if (operatorMaterials.has(key)) return operatorMaterials.get(key);
  const isCt = Number(team) === 3;
  const material = new THREE.MeshPhysicalMaterial({
    // Vertex colors are sampled from each official base-color material. Keep
    // the team tint deliberately subtle so cloth, skin, armour and equipment
    // retain their real appearance under the scene lights.
    color: isCt ? 0x7895a8 : 0xa27d58,
    vertexColors: true,
    emissive: local ? 0x113c2b : isCt ? 0x0a2035 : 0x321b08,
    emissiveIntensity: local ? 0.09 : 0.035,
    roughness: 0.76,
    metalness: 0.035,
    clearcoat: 0.025,
    clearcoatRoughness: 0.84,
    sheen: 0.14,
    sheenRoughness: 0.88,
    sheenColor: isCt ? 0x8096aa : 0xa58364,
    envMapIntensity: 0.42,
    transparent: false,
    opacity: 1,
    depthTest: true,
    depthWrite: true,
    side: THREE.FrontSide,
  });
  material.onBeforeCompile = (shader) => {
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <roughnessmap_fragment>', `#include <roughnessmap_fragment>
#if defined(USE_COLOR) || defined(USE_COLOR_ALPHA)
  float powVertexLuma = dot(vColor.rgb, vec3(0.2126, 0.7152, 0.0722));
  float powHardSurface = 1.0 - smoothstep(0.16, 0.46, powVertexLuma);
  roughnessFactor = clamp(roughnessFactor - powHardSurface * 0.16, 0.46, 0.96);
#endif`)
      .replace('#include <metalnessmap_fragment>', `#include <metalnessmap_fragment>
#if defined(USE_COLOR) || defined(USE_COLOR_ALPHA)
  metalnessFactor = clamp(metalnessFactor + powHardSurface * 0.075, 0.0, 0.16);
#endif`);
  };
  material.customProgramCacheKey = () => 'pow-operator-pbr-v2';
  operatorMaterials.set(key, material);
  return material;
}

function equipmentMaterial(name, color, metalness = 0.2, roughness = 0.62) {
  const key = `${name}:${color}:${metalness}:${roughness}`;
  if (operatorEquipmentMaterials.has(key)) return operatorEquipmentMaterials.get(key);
  const material = new THREE.MeshStandardMaterial({ color, metalness, roughness });
  operatorEquipmentMaterials.set(key, material);
  return material;
}

function scaledEquipmentMesh(geometry, material, scale, position, rotation = null) {
  const mesh = new THREE.Mesh(geometry, material);
  mesh.scale.set(...scale);
  mesh.position.set(...position);
  if (rotation) mesh.rotation.set(...rotation);
  mesh.castShadow = false;
  mesh.receiveShadow = false;
  return mesh;
}

const OFFICIAL_WEAPON_CONFIG = Object.freeze({
  ak47: { position: [-0.15, 1.23, 0], rotation: [0, -Math.PI / 2, 0], scale: 1.05 },
  m4a4: { position: [0.15, 1.23, 0], rotation: [0, -Math.PI / 2, 0], scale: 1.02 },
  flashbang: { position: [0, 0, 0], rotation: [0.18, 0, -0.12], scale: 1.08 },
  smokegrenade: { position: [0, 0, 0], rotation: [0.12, 0, -0.10], scale: 1.12 },
  hegrenade: { position: [0, 0, 0], rotation: [0.16, 0, -0.11], scale: 1.10 },
  c4: { position: [-0.28, 0.94, -0.02], rotation: [0, Math.PI / 2, 0], scale: 1.08 },
});

function createOfficialAsset(assetId) {
  const template = officialWeaponTemplates.get(assetId);
  const config = OFFICIAL_WEAPON_CONFIG[assetId];
  if (!template || !config) return null;
  const group = new THREE.Group();
  const model = template.clone(true);
  group.name = `official-${assetId}`;
  group.position.set(...config.position);
  group.rotation.set(...config.rotation);
  group.scale.setScalar(config.scale);
  model.traverse((object) => {
    if (!object.isMesh) return;
    object.castShadow = false;
    object.receiveShadow = false;
    object.renderOrder = 23;
    if (object.material) {
      object.material.depthTest = true;
      object.material.depthWrite = true;
      object.material.envMapIntensity = 0.54;
    }
  });
  group.add(model);
  return group;
}

function createFallbackWeaponProp(weapon, team) {
  const group = new THREE.Group();
  group.name = 'fallback-held-weapon';
  group.scale.setScalar(0.86);
  group.position.y = 0.172;
  const isAk = String(weapon).toLowerCase().includes('ak');
  const receiver = equipmentMaterial('rifle-receiver', 0x20242a, 0.68, 0.34);
  const polymer = equipmentMaterial(
    isAk ? 'ak-furniture' : 'm4-furniture',
    Number(team) === 2 && isAk ? 0x62401f : 0x25313c,
    0.05,
    0.76,
  );
  const detail = equipmentMaterial('rifle-detail', 0x0f1216, 0.76, 0.27);
  group.add(
    scaledEquipmentMesh(operatorEquipmentGeometry.box, receiver, [0.44, 0.09, 0.08], [-0.30, 1.23, 0]),
    scaledEquipmentMesh(operatorEquipmentGeometry.barrel, receiver, [0.022, 0.40, 0.022], [-0.70, 1.23, 0], [0, 0, Math.PI / 2]),
    scaledEquipmentMesh(operatorEquipmentGeometry.box, polymer, [0.25, 0.12, 0.10], [0.05, 1.22, 0]),
    scaledEquipmentMesh(operatorEquipmentGeometry.box, polymer, [0.22, 0.10, 0.08], [-0.53, 1.22, 0]),
    scaledEquipmentMesh(operatorEquipmentGeometry.box, polymer, [0.10, 0.25, 0.065], [-0.25, 1.08, isAk ? -0.015 : 0], [0, 0, isAk ? -0.24 : -0.08]),
    scaledEquipmentMesh(operatorEquipmentGeometry.box, receiver, [0.11, 0.07, 0.055], [-0.20, 1.13, -0.10], [0.18, 0, 0]),
    scaledEquipmentMesh(operatorEquipmentGeometry.box, detail, [0.055, 0.055, 0.055], [-0.94, 1.23, 0]),
    scaledEquipmentMesh(operatorEquipmentGeometry.box, detail, [0.035, 0.065, 0.025], [-0.49, 1.32, 0]),
    scaledEquipmentMesh(operatorEquipmentGeometry.box, detail, [0.035, 0.055, 0.025], [-0.16, 1.32, 0]),
    scaledEquipmentMesh(operatorEquipmentGeometry.box, polymer, [0.075, 0.19, 0.06], [-0.03, 1.09, 0.015], [0, 0, -0.30]),
  );
  group.traverse((object) => { object.renderOrder = 22; });
  return group;
}

function createWeaponProp(weapon, team) {
  const normalized = String(weapon || '').toLowerCase();
  const assetId = normalized.includes('ak') ? 'ak47' : normalized.includes('m4') ? 'm4a4' : null;
  const official = assetId ? createOfficialAsset(assetId) : null;
  if (official) {
    official.name = 'held-weapon';
    return official;
  }
  return createFallbackWeaponProp(weapon, team);
}

function attachOperatorEquipment(operator, player) {
  const weapon = createWeaponProp(player.weapon, player.team);
  operator.add(weapon);
  const throwableProps = new Map();
  const hand = operator.userData.poseRig?.byName?.get('hand_R');
  ['flashbang', 'smokegrenade', 'hegrenade'].forEach((assetId) => {
    const throwable = createOfficialAsset(assetId);
    if (!throwable) return;
    throwable.name = `throwable-${assetId}`;
    throwable.position.set(0, 0, 0);
    throwable.visible = false;
    if (hand) hand.add(throwable);
    else operator.add(throwable);
    throwableProps.set(assetId, throwable);
  });
  if (!throwableProps.size) {
    const fallbackGrenade = new THREE.Mesh(
      operatorEquipmentGeometry.grenade,
      equipmentMaterial('grenade-shell', 0x526148, 0.35, 0.72),
    );
    fallbackGrenade.name = 'throwable-fallback';
    fallbackGrenade.visible = false;
    fallbackGrenade.renderOrder = 23;
    if (hand) hand.add(fallbackGrenade);
    else {
      fallbackGrenade.position.set(-0.22, 1.35, -0.40);
      operator.add(fallbackGrenade);
    }
    throwableProps.set('flashbang', fallbackGrenade);
  }
  operator.userData.weaponProp = weapon;
  operator.userData.weaponBindTransform = {
    position: weapon.position.clone(),
    quaternion: weapon.quaternion.clone(),
  };
  operator.userData.grenadeProps = throwableProps;
  const bomb = createOfficialAsset('c4') || new THREE.Group();
  bomb.name = 'bomb-device';
  if (!bomb.children.length) {
    bomb.add(
      scaledEquipmentMesh(operatorEquipmentGeometry.box, equipmentMaterial('bomb-case', 0x182018, 0.32, 0.72), [0.25, 0.12, 0.18], [-0.29, 0.72, 0]),
      scaledEquipmentMesh(operatorEquipmentGeometry.box, equipmentMaterial('bomb-keypad', 0x8c2d24, 0.18, 0.58), [0.13, 0.018, 0.10], [-0.29, 0.785, -0.02]),
    );
  }
  bomb.visible = false;
  bomb.traverse((object) => { object.renderOrder = 23; });
  operator.add(bomb);
  operator.userData.bombProp = bomb;
}

const poseEuler = new THREE.Euler();
const poseQuaternion = new THREE.Quaternion();
const ikJointPosition = new THREE.Vector3();
const ikEndPosition = new THREE.Vector3();
const ikTargetWorld = new THREE.Vector3();
const ikFrom = new THREE.Vector3();
const ikTo = new THREE.Vector3();
const ikDelta = new THREE.Quaternion();
const ikJointWorld = new THREE.Quaternion();
const ikParentWorld = new THREE.Quaternion();
const ikTargetLocal = new THREE.Vector3();
const armTargetRight = new THREE.Vector3();
const armTargetLeft = new THREE.Vector3();
const throwWindupTarget = new THREE.Vector3(0.14, 1.82, -0.34);
const throwReleaseTarget = new THREE.Vector3(-0.63, 1.57, -0.10);

function resetOperatorPose(rig) {
  rig.bones.forEach((bone, index) => {
    const bind = rig.bindPose[index];
    bone.position.copy(bind.position);
    bone.quaternion.copy(bind.quaternion);
    bone.scale.copy(bind.scale);
  });
}

function rotatePoseBone(rig, name, x = 0, y = 0, z = 0) {
  const index = rig.indexByName.get(name);
  if (!Number.isInteger(index)) return;
  poseEuler.set(x, y, z, 'XYZ');
  poseQuaternion.setFromEuler(poseEuler);
  rig.bones[index].quaternion.copy(rig.bindPose[index].quaternion).multiply(poseQuaternion);
}

function rotateBoneToward(operator, joint, endEffector, targetLocal) {
  if (!joint || !endEffector || !joint.parent) return;
  operator.updateWorldMatrix(true, true);
  joint.getWorldPosition(ikJointPosition);
  endEffector.getWorldPosition(ikEndPosition);
  ikTargetWorld.copy(targetLocal);
  operator.localToWorld(ikTargetWorld);
  ikFrom.copy(ikEndPosition).sub(ikJointPosition).normalize();
  ikTo.copy(ikTargetWorld).sub(ikJointPosition).normalize();
  if (!ikFrom.lengthSq() || !ikTo.lengthSq()) return;
  ikDelta.setFromUnitVectors(ikFrom, ikTo);
  joint.getWorldQuaternion(ikJointWorld);
  joint.parent.getWorldQuaternion(ikParentWorld);
  joint.quaternion.copy(ikParentWorld.invert().multiply(ikDelta.multiply(ikJointWorld))).normalize();
}

function solveArmTarget(operator, side, target) {
  const rig = operator.userData.poseRig;
  const upper = rig.byName.get(`arm_upper_${side}`);
  const lower = rig.byName.get(`arm_lower_${side}`);
  const hand = rig.byName.get(`hand_${side}`);
  if (!upper || !lower || !hand) return;
  for (let iteration = 0; iteration < 4; iteration += 1) {
    rotateBoneToward(operator, lower, hand, target);
    rotateBoneToward(operator, upper, hand, target);
  }
}

function dampValue(current, target, responsiveness, deltaSeconds) {
  return THREE.MathUtils.lerp(current, target, 1 - Math.exp(-responsiveness * deltaSeconds));
}

function gaitFootLift(phase) {
  const normalized = ((phase / (Math.PI * 2)) % 1 + 1) % 1;
  return normalized < 0.5 ? Math.sin(normalized * Math.PI * 2) : 0;
}

function applyOperatorArmTargets(operator, animation, phase, stride, breathing) {
  const walkBob = Math.abs(Math.sin(phase * 2)) * 0.020 * stride * animation.locomotionBlend
    + breathing * 0.38 * (1 - animation.locomotionBlend);
  armTargetRight.set(-0.08, 1.20 + walkBob, -0.065);
  armTargetLeft.set(-0.48, 1.23 + walkBob, 0.035);
  if (animation.plantBlend > 0.001) {
    armTargetRight.lerp(ikTargetLocal.set(-0.16, 0.95, -0.10), animation.plantBlend);
    armTargetLeft.lerp(ikTargetLocal.set(-0.40, 0.94, 0.08), animation.plantBlend);
  }
  if (animation.victoryBlend > 0.001) {
    armTargetRight.lerp(ikTargetLocal.set(0.16, 1.86, -0.10), animation.victoryBlend);
    armTargetLeft.lerp(ikTargetLocal.set(-0.30, 1.78, 0.08), animation.victoryBlend);
  }
  if (animation.throwBlend > 0.001) {
    const throwProgress = THREE.MathUtils.clamp(animation.actionProgress, 0, 1);
    if (throwProgress < 0.46) {
      const blend = throwProgress / 0.46;
      ikTargetLocal.lerpVectors(
        ikTargetLocal.set(-0.08, 1.28, -0.08),
        throwWindupTarget,
        blend,
      );
    } else {
      const blend = (throwProgress - 0.46) / 0.54;
      ikTargetLocal.lerpVectors(
        ikTargetLocal.set(0.14, 1.82, -0.34),
        throwReleaseTarget,
        blend,
      );
    }
    armTargetRight.lerp(ikTargetLocal, animation.throwBlend);
    armTargetLeft.lerp(ikTargetLocal.set(-0.14, 1.26, 0.04), animation.throwBlend);
  }
  solveArmTarget(operator, 'R', armTargetRight);
  solveArmTarget(operator, 'L', armTargetLeft);
}

function applyOperatorAnimation(operator, time) {
  const rig = operator.userData.poseRig;
  const snapshot = operator.userData.animationSnapshot;
  if (!rig || !snapshot) return;
  resetOperatorPose(rig);

  const action = snapshot.action || 'idle';
  const observedSpeed = Math.max(0, Number(snapshot.speedUnitsPerSecond) || 0);
  const gaitCycleUnits = THREE.MathUtils.clamp(Number(snapshot.gaitCycleUnits) || 128, 96, 160);
  const animationClockPaused = demoMode && demoPaused;
  const frameAge = animationClockPaused ? 0 : Math.max(0, time - Number(snapshot.updatedAt || time));
  const observedCycle = Number(snapshot.locomotionCycle || 0)
    + frameAge / 1000 * observedSpeed / gaitCycleUnits;
  const animation = operator.userData.animationState || {
    lastTime: time,
    snapshotUpdatedAt: Number(snapshot.updatedAt || time),
    cycle: observedCycle,
    gaitCycleUnits,
    phaseError: 0,
    speed: observedSpeed,
    locomotionBlend: ['walk', 'run'].includes(action) ? 1 : 0,
    plantBlend: ['plant', 'defuse'].includes(action) ? 1 : 0,
    throwBlend: action === 'throw' ? 1 : 0,
    fireBlend: action === 'fire' ? 1 : 0,
    reloadBlend: action === 'reload' ? 1 : 0,
    scopedBlend: action === 'scoped' ? 1 : 0,
    hitBlend: ['hit', 'flashed'].includes(action) ? 1 : 0,
    deathBlend: action === 'death' ? 1 : 0,
    victoryBlend: action === 'victory' ? 1 : 0,
    actionProgress: Number(snapshot.actionProgress) || 0,
    turnLean: 0,
  };
  operator.userData.animationState = animation;
  const deltaSeconds = animationClockPaused
    ? 0
    : THREE.MathUtils.clamp((time - animation.lastTime) / 1000, 0, 0.20);
  animation.lastTime = time;
  animation.speed = dampValue(animation.speed, observedSpeed, 10, deltaSeconds);
  animation.gaitCycleUnits = dampValue(animation.gaitCycleUnits, gaitCycleUnits, 8, deltaSeconds);
  animation.cycle += deltaSeconds * animation.speed / Math.max(96, animation.gaitCycleUnits);
  if (Number(snapshot.updatedAt || 0) !== animation.snapshotUpdatedAt) {
    let phaseError = ((observedCycle - animation.cycle + 0.5) % 1 + 1) % 1 - 0.5;
    if (!Number.isFinite(phaseError)) phaseError = 0;
    animation.phaseError = THREE.MathUtils.clamp(
      Number(animation.phaseError || 0) + phaseError,
      -0.18,
      0.18,
    );
    animation.snapshotUpdatedAt = Number(snapshot.updatedAt || time);
  }
  const phaseCorrection = THREE.MathUtils.clamp(Number(animation.phaseError || 0), -0.018, 0.018);
  animation.cycle += phaseCorrection;
  animation.phaseError -= phaseCorrection;
  animation.locomotionBlend = dampValue(animation.locomotionBlend, ['walk', 'run'].includes(action) ? 1 : 0, 9, deltaSeconds);
  animation.plantBlend = dampValue(animation.plantBlend, ['plant', 'defuse'].includes(action) ? 1 : 0, 11, deltaSeconds);
  animation.throwBlend = dampValue(animation.throwBlend, action === 'throw' ? 1 : 0, 14, deltaSeconds);
  animation.fireBlend = dampValue(animation.fireBlend, action === 'fire' ? 1 : 0, 22, deltaSeconds);
  animation.reloadBlend = dampValue(animation.reloadBlend, action === 'reload' ? 1 : 0, 10, deltaSeconds);
  animation.scopedBlend = dampValue(animation.scopedBlend, action === 'scoped' ? 1 : 0, 12, deltaSeconds);
  animation.hitBlend = dampValue(animation.hitBlend, ['hit', 'flashed'].includes(action) ? 1 : 0, 18, deltaSeconds);
  animation.deathBlend = dampValue(animation.deathBlend, action === 'death' ? 1 : 0, 9, deltaSeconds);
  animation.victoryBlend = dampValue(animation.victoryBlend, action === 'victory' ? 1 : 0, 10, deltaSeconds);
  animation.actionProgress = dampValue(
    animation.actionProgress,
    Number(snapshot.actionProgress) || 0,
    action === 'throw' ? 18 : 10,
    deltaSeconds,
  );
  animation.turnLean = dampValue(
    animation.turnLean,
    THREE.MathUtils.clamp(Number(snapshot.turnRateDegreesPerSecond) || 0, -240, 240) / 240,
    8,
    deltaSeconds,
  );
  if (animationClockPaused) {
    animation.locomotionBlend = ['walk', 'run'].includes(action) ? 1 : 0;
    animation.plantBlend = ['plant', 'defuse'].includes(action) ? 1 : 0;
    animation.throwBlend = action === 'throw' ? 1 : 0;
    animation.fireBlend = action === 'fire' ? 1 : 0;
    animation.reloadBlend = action === 'reload' ? 1 : 0;
    animation.scopedBlend = action === 'scoped' ? 1 : 0;
    animation.hitBlend = ['hit', 'flashed'].includes(action) ? 1 : 0;
    animation.deathBlend = action === 'death' ? 1 : 0;
    animation.victoryBlend = action === 'victory' ? 1 : 0;
    animation.actionProgress = Number(snapshot.actionProgress) || 0;
  }
  const phase = animation.cycle * Math.PI * 2;
  const swing = Math.sin(phase);
  const locomotionBlend = animation.locomotionBlend;
  const locomoting = locomotionBlend > 0.001;
  const runBlend = THREE.MathUtils.smoothstep(animation.speed, 128, 205);
  const stride = Math.min(1.08, animation.speed / 205);
  const breathing = Math.sin(time * 0.0024) * 0.018;
  const turnLean = animation.turnLean;
  const plant = Math.sin(animation.plantBlend * Math.PI / 2);
  const fireRecoil = Math.sin(THREE.MathUtils.clamp(animation.actionProgress, 0, 1) * Math.PI * 4) ** 2
    * animation.fireBlend;
  const reloadMotion = Math.sin(THREE.MathUtils.clamp(animation.actionProgress, 0, 1) * Math.PI)
    * animation.reloadBlend;
  const scopedAim = animation.scopedBlend;
  const hitReaction = Math.sin(THREE.MathUtils.clamp(animation.actionProgress, 0, 1) * Math.PI)
    * animation.hitBlend;
  const deathFall = THREE.MathUtils.smoothstep(animation.deathBlend, 0, 1);
  const victoryLift = Math.sin(THREE.MathUtils.clamp(animation.victoryBlend, 0, 1) * Math.PI / 2);

  operator.rotation.z = -1.52 * deathFall;
  operator.position.x = 0.18 * deathFall;
  operator.position.y = -0.08 * deathFall;

  const pelvisIndex = rig.indexByName.get('pelvis');
  if (Number.isInteger(pelvisIndex)) {
    rig.bones[pelvisIndex].position.y += Math.abs(Math.sin(phase * 2))
      * (0.022 + runBlend * 0.014) * stride * locomotionBlend
      + breathing * (1 - locomotionBlend)
      - 0.38 * plant;
  }
  rotatePoseBone(rig, 'spine_1', -0.06 * fireRecoil, 0, -0.20 * plant + 0.20 * hitReaction + 0.06 * reloadMotion);
  rotatePoseBone(rig, 'spine_2', runBlend * 0.05 * locomotionBlend - 0.10 * fireRecoil - 0.04 * scopedAim, turnLean * 0.045, -0.06 * stride * locomotionBlend + breathing * 0.6 * (1 - locomotionBlend) - 0.16 * hitReaction);
  rotatePoseBone(rig, 'spine_3', 0, 0, -0.08 * victoryLift);
  rotatePoseBone(rig, 'neck_0', 0, 0, -breathing * 0.35);

  if (locomoting) {
    const hipSwing = (0.34 + runBlend * 0.12) * swing * stride * locomotionBlend;
    const leftFootLift = gaitFootLift(phase + Math.PI);
    const rightFootLift = gaitFootLift(phase);
    const leftKnee = leftFootLift * (0.34 + runBlend * 0.14) * stride * locomotionBlend;
    const rightKnee = rightFootLift * (0.34 + runBlend * 0.14) * stride * locomotionBlend;
    rotatePoseBone(rig, 'leg_upper_L', 0, 0, hipSwing + 0.42 * plant);
    rotatePoseBone(rig, 'leg_upper_R', 0, 0, -hipSwing - 0.42 * plant);
    rotatePoseBone(rig, 'leg_lower_L', 0, 0, leftKnee - 0.52 * plant);
    rotatePoseBone(rig, 'leg_lower_R', 0, 0, rightKnee + 0.52 * plant);
    // Counter-rotate planted feet so the sole remains visually close to the
    // navigation surface instead of skating with the upper leg swing.
    rotatePoseBone(rig, 'ankle_L', 0, 0, -hipSwing * 0.52 - leftKnee * 0.26 + leftFootLift * 0.14);
    rotatePoseBone(rig, 'ankle_R', 0, 0, hipSwing * 0.52 - rightKnee * 0.26 + rightFootLift * 0.14);
    rotatePoseBone(rig, 'pelvis', 0, 0.030 * swing * stride, -turnLean * 0.035);
  } else if (plant > 0.001) {
    rotatePoseBone(rig, 'leg_upper_L', 0, 0, 0.42 * plant);
    rotatePoseBone(rig, 'leg_upper_R', 0, 0, -0.42 * plant);
    rotatePoseBone(rig, 'leg_lower_L', 0, 0, -0.52 * plant);
    rotatePoseBone(rig, 'leg_lower_R', 0, 0, 0.52 * plant);
  }

  if (animation.throwBlend > 0.001) {
    const windup = Math.sin(THREE.MathUtils.clamp(animation.actionProgress, 0, 1) * Math.PI) * animation.throwBlend;
    rotatePoseBone(rig, 'spine_3', 0, 0.12 * windup, -0.08 * windup);
  }

  applyOperatorArmTargets(operator, animation, phase, stride, breathing);

  const weaponProp = operator.userData.weaponProp;
  const weaponBind = operator.userData.weaponBindTransform;
  if (weaponProp && weaponBind) {
    weaponProp.position.copy(weaponBind.position);
    weaponProp.quaternion.copy(weaponBind.quaternion);
    if (locomoting) {
      weaponProp.position.y += Math.abs(Math.sin(phase * 2)) * 0.014 * stride * locomotionBlend;
      weaponProp.position.z += swing * 0.008 * stride * locomotionBlend;
      weaponProp.rotation.z += swing * (0.008 + runBlend * 0.006) * stride * locomotionBlend;
    } else {
      weaponProp.position.y += breathing * 0.38;
      weaponProp.rotation.z += breathing * 0.10;
    }
    weaponProp.position.z += fireRecoil * 0.075;
    weaponProp.rotation.x -= fireRecoil * 0.055;
    weaponProp.position.y += scopedAim * 0.10 - reloadMotion * 0.10;
    weaponProp.position.z -= scopedAim * 0.06;
    weaponProp.rotation.z -= reloadMotion * 0.32;
    weaponProp.visible = animation.throwBlend < 0.12 && animation.plantBlend < 0.12 && deathFall < 0.35;
  }
  const throwableId = String(snapshot.utilityType || '').toLowerCase().includes('smoke')
    ? 'smokegrenade'
    : String(snapshot.utilityType || '').toLowerCase().includes('he')
      ? 'hegrenade'
    : 'flashbang';
  operator.userData.grenadeProps?.forEach((prop, assetId) => {
    prop.visible = animation.throwBlend > 0.12
      && animation.actionProgress < 0.62
      && (assetId === throwableId || !operator.userData.grenadeProps.has(throwableId));
  });
  operator.userData.bombProp.visible = animation.plantBlend > 0.12;
  operator.skeleton.update();
}

function updateOperatorAnimations(time) {
  playerEntityRoot.children.forEach((root) => {
    const operator = root.userData.operator;
    if (!operator || !root.visible) return;
    const distance = camera.position.distanceTo(root.position);
    const interval = focusedPlayerId === String(root.userData.player?.id)
      ? 0
      : distance > 70 ? 180 : distance > 35 ? 80 : 0;
    if (time < Number(root.userData.nextAnimationUpdateAt || 0)) return;
    root.userData.nextAnimationUpdateAt = time + interval;
    applyOperatorAnimation(operator, time);
  });
}

async function loadOfficialWeaponModels() {
  const manifestResponse = await fetch('./static/models/weapons/manifest.v1.json');
  if (!manifestResponse.ok) throw new Error(`official weapon manifest HTTP ${manifestResponse.status}`);
  const manifest = await manifestResponse.json();
  if (manifest?.schema !== 'csgo2-radar/official-weapon-assets/v1' || !Array.isArray(manifest.assets)) {
    throw new Error('official weapon manifest schema mismatch');
  }
  const loader = new GLTFLoader();
  const loadAsset = async (asset) => {
    const assetId = String(asset.id || '');
    const uri = String(asset.uri || '');
    if (!OFFICIAL_WEAPON_CONFIG[assetId] || !uri.startsWith('models/weapons/') || uri.includes('..')) {
      throw new Error(`invalid official weapon asset ${assetId}`);
    }
    const version = String(asset.sha256 || '').slice(0, 16).toLowerCase();
    const gltf = await loader.loadAsync(`./static/${uri}?v=${version}`);
    gltf.scene.name = `official-${assetId}-template`;
    gltf.scene.updateMatrixWorld(true);
    officialWeaponTemplates.set(assetId, gltf.scene);
    console.info(`[CS2RADAR] official ${assetId} ready (${asset.bytes} bytes)`);
  };
  const loadBatch = async (assets) => {
    const results = await Promise.allSettled(assets.map(loadAsset));
    results.forEach((result, index) => {
      if (result.status === 'rejected') {
        console.warn(`[CS2RADAR] official ${assets[index]?.id || index} unavailable`, result.reason);
      }
    });
  };
  const primary = manifest.assets.filter((asset) => ['ak47', 'm4a4'].includes(asset.id));
  const deferred = manifest.assets.filter((asset) => !['ak47', 'm4a4'].includes(asset.id));
  await loadBatch(primary);
  officialWeaponState = officialWeaponTemplates.size ? 'partial' : 'fallback';
  const loadDeferred = async () => {
    await loadBatch(deferred);
    officialWeaponState = officialWeaponTemplates.size === manifest.assets.length ? 'ready' : 'partial';
    if (currentFrame) renderEntities(currentFrame);
  };
  if ('requestIdleCallback' in window) window.requestIdleCallback(() => void loadDeferred(), { timeout: 1800 });
  else setTimeout(() => void loadDeferred(), 350);
}

async function loadOperatorModels() {
  const definitions = [
    ['ctm_sas', './static/models/ctm_sas.powmesh', 'SAS'],
    ['tm_phoenix', './static/models/tm_phoenix.powmesh', 'Phoenix'],
  ];
  const results = await Promise.allSettled(definitions.map(async ([side, url, name]) => {
    const geometry = await loadPowOperatorGeometry(url);
    if (!geometry.userData.vertexColors) throw new Error(`${name} is missing baked material colors`);
    operatorGeometries.set(side, geometry);
    console.info(`[CS2RADAR] official ${name} operator ready (${geometry.userData.triangles} triangles)`);
  }));
  operatorGeometryState = operatorGeometries.size === definitions.length ? 'ready' : operatorGeometries.size ? 'partial' : 'fallback';
  results.forEach((result, index) => {
    if (result.status === 'rejected') {
      console.warn(`[CS2RADAR] ${definitions[index][2]} operator unavailable`, result.reason);
    }
  });
  try {
    await loadOfficialWeaponModels();
  } catch (error) {
    officialWeaponState = 'fallback';
    console.warn('[CS2RADAR] official weapon models unavailable', error);
  }
  if (currentFrame) renderEntities(currentFrame);
}

function activeLayerOpacity(z) {
  if (!activeLayer || !Number.isFinite(Number(z))) return 1;
  const minimum = Number(activeLayer.zMin);
  const maximum = Number(activeLayer.zMax);
  return Number(z) >= minimum && Number(z) <= maximum ? 1 : 0.24;
}

function resetPlayerTrails() {
  playerTrails.clear();
}

function updatePlayerTrails(frame) {
  const liveIds = new Set();
  frame.players.forEach((player) => {
    if (player.dead) return;
    const id = String(player.id);
    liveIds.add(id);
    const trail = playerTrails.get(id) || [];
    const previous = trail.at(-1);
    const next = {
      x: Number(player.position.x),
      y: Number(player.position.y),
      z: Number(player.position.z),
    };
    if (!previous || Math.hypot(next.x - previous.x, next.y - previous.y, next.z - previous.z) >= 2) {
      trail.push(next);
      if (trail.length > TRAIL_POINT_LIMIT) trail.splice(0, trail.length - TRAIL_POINT_LIMIT);
    }
    playerTrails.set(id, trail);
  });
  for (const id of playerTrails.keys()) if (!liveIds.has(id)) playerTrails.delete(id);
}

async function loadImage(url) {
  const image = new Image();
  image.decoding = 'async';
  image.src = url;
  await image.decode();
  return image;
}

function versionedResourceUrl(uri, base, digest) {
  const url = new URL(uri, base);
  if (digest) url.searchParams.set('v', String(digest).slice(0, 16));
  return url;
}

async function resourceHashMatches(buffer, expectedHash) {
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  const actual = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
  return actual === String(expectedHash || '').toLowerCase();
}

async function loadMapRegistry() {
  const response = await fetch('./static/maps/map-registry.v1.json?v=0.24.0', { cache: 'no-store' });
  if (!response.ok) throw new Error(`CS2RADAR-REGISTRY-201: HTTP ${response.status}`);
  mapRegistry = validateRegistry(await response.json());
  ui.mapSelect.replaceChildren(...mapRegistry.maps.map((entry) => {
    const option = document.createElement('option');
    option.value = entry.mapId;
    const evidence = entry.liveValidation?.status ? '实机验证' : (entry.status === 'validated' ? '已校准' : '待采集');
    option.textContent = `${entry.displayName} · ${evidence}`;
    option.disabled = !entry.packageUri;
    return option;
  }));
  ui.mapSelect.value = mapRegistry.defaultMapId;
}

function disposeCurrentMap() {
  clearTacticalAnnotations3d();
  if (mapMesh) {
    scene.remove(mapMesh);
    mapMesh.geometry?.dispose?.();
    disposeMapMaterial(mapMesh.material);
  }
  if (mapEdges) {
    scene.remove(mapEdges);
    mapEdges.geometry?.dispose?.();
    mapEdges.material?.dispose?.();
  }
  radarTexture?.dispose?.();
  buyzoneTexture?.dispose?.();
  radarTexture = null;
  buyzoneTexture = null;
  buyzoneImage = null;
  radarProjection = null;
  tacticalAnnotations = null;
  mapLiveValidation = null;
  mapResourceBundle = null;
  mapDemoScenarios = null;
  mapGrenadeKnowledge = null;
  mapDynamicEntities = null;
  currentMapBase = null;
  geometryLoadPromise = null;
  mapMesh = null;
  mapEdges = null;
  mapSurfaceData = null;
  mapTriangleCount = 0;
  resetPlayerTrails();
}

function availableLayers() {
  return floorIndex?.layers || mapManifest?.calibration?.layers || calibration?.layers || [];
}

function describeLayerSelection(reason, sourceZ = null) {
  if (!ui.layerState) return;
  if (ui.layerMode?.value === 'manual') {
    ui.layerState.textContent = '手动锁定，不跟随玩家高度';
    return;
  }
  const hasHeight = sourceZ !== null && sourceZ !== undefined && Number.isFinite(Number(sourceZ));
  const height = hasHeight ? ` · Z ${Math.round(Number(sourceZ))}` : '';
  const labels = { inside: '自动匹配', hysteresis: '防抖保持', nearest: '最近校准层' };
  ui.layerState.textContent = `${labels[reason] || '等待本机玩家'}${height}`;
}

function populateLayerControls() {
  if (!ui.layerSelect) return;
  const layers = availableLayers();
  ui.layerSelect.replaceChildren(...layers.map((layer) => {
    const option = document.createElement('option');
    option.value = layer.id;
    option.textContent = `${layer.displayName || layer.id} · Z ${Math.round(layer.zMin)}–${Math.round(layer.zMax)}`;
    return option;
  }));
  ui.layerSelect.disabled = ui.layerMode?.value !== 'manual' || layers.length < 2;
}

function setActiveLayer(layer, reason = 'initial', sourceZ = null) {
  if (!layer) return;
  const changed = activeLayer?.id !== layer.id;
  activeLayer = layer;
  ui.activeLayer.textContent = layer.id === 'default' ? '主层' : layer.displayName || layer.id;
  if (ui.layerSelect) ui.layerSelect.value = layer.id;
  describeLayerSelection(reason, sourceZ);
  applyLayerProjection(layer);
  renderTacticalAnnotations3d();
  draw2d();
  if (!changed) return;
  const minimum = Math.floor(Number(layer.zMin));
  const maximum = Math.ceil(Number(layer.zMax));
  ui.heightCut.min = String(minimum);
  ui.heightCut.max = String(maximum);
  // A newly loaded map starts fully visible. Reusing the previous map's cut height
  // makes high-rise packages (notably de_vertigo) appear as a single wall slice.
  ui.heightCut.value = String(maximum);
  updateClipping();
}

function installMapGeometry(parsed, normalized, surfaceData = null) {
  if (mapMesh) {
    scene.remove(mapMesh);
    mapMesh.geometry?.dispose?.();
    disposeMapMaterial(mapMesh.material);
  }
  if (mapEdges) {
    scene.remove(mapEdges);
    mapEdges.geometry?.dispose?.();
    mapEdges.material?.dispose?.();
  }
  if (surfaceData) applyTriangleSurfaceGroups(parsed.geometry, surfaceData);
  mapSurfaceData = surfaceData;
  const material = createRadarMaterials(parsed.geometry.boundingBox, ui.renderStyle?.value, normalized);
  mapMesh = new THREE.Mesh(parsed.geometry, material);
  mapMesh.rotation.x = -Math.PI / 2;
  mapMesh.scale.setScalar(SOURCE_TO_METRES);
  mapMesh.name = `${normalized}-graybox`;
  scene.add(mapMesh);
  mapTriangleCount = parsed.triangleCount;
  if (parsed.triangleCount <= EDGE_TRIANGLE_BUDGET) {
    const edgeGeometry = new THREE.EdgesGeometry(parsed.geometry, 42);
    const edgeStyle = resolveRenderProfile(normalized, ui.renderStyle?.value);
    mapEdges = new THREE.LineSegments(edgeGeometry, new THREE.LineBasicMaterial({
      color: edgeStyle.edge,
      transparent: true,
      opacity: edgeStyle.outlineOpacity,
      depthWrite: false,
      clippingPlanes: ui.roofCut?.checked ? [clippingPlane, mapContextFloorPlane] : [],
    }));
    mapEdges.rotation.copy(mapMesh.rotation);
    mapEdges.scale.copy(mapMesh.scale);
    mapEdges.renderOrder = 2;
    mapEdges.name = `${normalized}-structure-outline`;
    scene.add(mapEdges);
  }
  applyRenderStyle();
  applyRenderQuality();
  updateClipping();
  fitMap();
  setGeometryLoadingState('ready');
}

function setGeometryLoadingState(state, detail = '') {
  if (!ui.geometryLoading) return;
  const visible = currentView === '3d' && !mapMesh && state !== 'ready';
  ui.geometryLoading.classList.toggle('hidden', !visible);
  ui.geometryLoading.classList.toggle('is-error', state === 'error');
  if (state === 'error') {
    ui.geometryLoadingTitle.textContent = '3D 地图载入失败';
    ui.geometryLoadingDetail.textContent = detail || '可以继续使用 2D 雷达，或稍后重试 3D 视图';
  } else {
    ui.geometryLoadingTitle.textContent = '战术场景已就绪';
    ui.geometryLoadingDetail.textContent = detail || '正在后台载入 3D 地图，人物与时间线可以立即预览';
  }
}

async function ensureGeometryLoaded() {
  if (mapMesh || !mapManifest || !currentMapBase || currentView !== '3d') return;
  if (geometryLoadPromise) return geometryLoadPromise;
  const expectedMapId = currentMapId;
  const expectedManifest = mapManifest;
  const geometryUrl = versionedResourceUrl(
    mapManifest.geometry.uri,
    currentMapBase,
    mapManifest.geometry.sha256,
  );
  const surfaceUrl = mapManifest.surfaceMaterials?.uri
    ? versionedResourceUrl(
      mapManifest.surfaceMaterials.uri,
      currentMapBase,
      mapManifest.surfaceMaterials.sha256,
    )
    : null;
  setGeometryLoadingState('loading');
  ui.frameState.textContent = `${mapManifest.displayName} · 2D 已就绪 · 正在渐进加载 3D`;
  const loadingPromise = (async () => {
    const surfaceRequest = surfaceUrl
      ? fetch(surfaceUrl, { cache: 'force-cache', signal: AbortSignal.timeout(5000) })
        .then(async (response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.arrayBuffer();
        })
        .catch((error) => {
          console.warn(`CS2RADAR-SURFACE-201: surface sidecar unavailable; using fallback material (${error.message})`);
          return null;
        })
      : Promise.resolve(null);
    const [response, surfaceBuffer] = await Promise.all([
      fetch(geometryUrl, { cache: 'force-cache' }),
      surfaceRequest,
    ]);
    if (mapManifest !== expectedManifest) return;
    if (!response.ok) throw new Error(`CS2RADAR-MAP-209: geometry HTTP ${response.status}`);
    const geometryBuffer = await response.arrayBuffer();
    if (mapManifest !== expectedManifest) return;
    const parsed = parseAwmh(geometryBuffer);
    let surfaceData = null;
    if (surfaceBuffer) {
      try {
        const hashesMatch = await Promise.all([
          resourceHashMatches(geometryBuffer, expectedManifest.geometry.sha256),
          resourceHashMatches(surfaceBuffer, expectedManifest.surfaceMaterials.sha256),
        ]);
        if (!hashesMatch.every(Boolean)) throw new Error('resource SHA-256 mismatch');
        surfaceData = parseTriangleSurfaces(surfaceBuffer, {
          triangleCount: parsed.triangleCount,
          geometrySha256: expectedManifest.geometry.sha256,
        });
      } catch (error) {
        console.warn(`CS2RADAR-SURFACE-202: invalid surface sidecar; using fallback material (${error.message})`);
      }
    }
    if (currentMapId !== expectedMapId || mapManifest !== expectedManifest) {
      parsed.geometry.dispose();
      return;
    }
    installMapGeometry(parsed, expectedMapId, surfaceData);
    const edgeMode = mapEdges?.visible ? '结构边线' : '清晰材质';
    const surfaceMode = surfaceData ? `${surfaceData.surfaceCount} 表面` : '单材质回退';
    ui.frameState.textContent = `${mapManifest.displayName} · ${parsed.triangleCount.toLocaleString()} 三角形 · ${surfaceMode} · ${edgeMode} · Build ${mapManifest.sourceBuildId}`;
  })().catch((error) => {
    if (mapManifest === expectedManifest) throw error;
  }).finally(() => {
    if (geometryLoadPromise === loadingPromise) geometryLoadPromise = null;
  });
  geometryLoadPromise = loadingPromise;
  return loadingPromise;
}

function queueGeometryLoad() {
  if (currentView !== '3d' || mapMesh) {
    setGeometryLoadingState('ready');
    return;
  }
  setGeometryLoadingState('loading');
  void ensureGeometryLoaded().catch((error) => {
    ui.frameState.textContent = error.message;
    setGeometryLoadingState('error', error.message);
    setStatus('error', '3D 资源加载失败', '3D');
  });
}

async function performMapLoad(mapId) {
  const normalized = normalizeMapId(mapId);
  const entry = findMapEntry(mapRegistry, normalized);
  if (!entry) throw new Error(`CS2RADAR-MAP-204: 未收录地图 ${normalized || 'unknown'}`);
  if (!entry.packageUri || entry.status !== 'validated') {
    throw new Error(`CS2RADAR-MAP-205: ${entry.displayName} 坐标仍待实测验收`);
  }

  setStatus('wait', `正在加载${entry.displayName}`, 'MAP');
  const manifestUrl = versionedResourceUrl(entry.packageUri, location.href, mapRegistry.sourceBuildId);
  const manifestResponse = await fetch(manifestUrl, { cache: 'no-store' });
  if (!manifestResponse.ok) throw new Error(`CS2RADAR-MAP-201: HTTP ${manifestResponse.status}`);
  const nextManifest = await manifestResponse.json();
  if (nextManifest.gameId !== 'csgo2' || normalizeMapId(nextManifest.mapId) !== normalized) {
    throw new Error('CS2RADAR-MAP-202: independent map boundary mismatch');
  }
  if (nextManifest.calibration?.status !== 'validated') {
    throw new Error(`CS2RADAR-MAP-206: ${normalized} calibration gate rejected package`);
  }

  const packageUrl = new URL(entry.packageUri, location.href);
  const base = new URL('./', packageUrl);
  const buyzoneUri = nextManifest.semanticOverlays?.buyzones?.uri;
  const annotationUri = nextManifest.semanticOverlays?.annotations?.uri;
  const floorIndexUri = nextManifest.floors?.uri;
  const liveValidationUri = nextManifest.liveValidation?.uri;
  const resourceBundleUri = nextManifest.resourceBundle?.uri;
  const [calibrationResponse, floorResponse, annotationResponse, liveValidationResponse, resourceBundleResponse, image, nextBuyzoneImage] = await Promise.all([
    fetch(new URL(nextManifest.calibration.uri, base), { cache: 'no-store' }),
    floorIndexUri ? fetch(new URL(floorIndexUri, base), { cache: 'no-store' }) : Promise.resolve(null),
    annotationUri ? fetch(new URL(annotationUri, base), { cache: 'no-store' }) : Promise.resolve(null),
    liveValidationUri ? fetch(new URL(liveValidationUri, base), { cache: 'no-store' }) : Promise.resolve(null),
    resourceBundleUri ? fetch(new URL(resourceBundleUri, base), { cache: 'no-store' }) : Promise.resolve(null),
    loadImage(versionedResourceUrl(nextManifest.radar.uri, base, nextManifest.radar.sha256)),
    buyzoneUri ? loadImage(versionedResourceUrl(
      buyzoneUri,
      base,
      nextManifest.semanticOverlays?.buyzones?.sha256,
    )) : Promise.resolve(null),
  ]);
  if (!calibrationResponse.ok || (floorResponse && !floorResponse.ok)
    || (annotationResponse && !annotationResponse.ok) || (liveValidationResponse && !liveValidationResponse.ok)
    || (resourceBundleResponse && !resourceBundleResponse.ok)) {
    throw new Error('CS2RADAR-MAP-203: map asset request failed');
  }
  const [calibrationData, floorData, annotationData, liveValidationData, resourceBundleData] = await Promise.all([
    calibrationResponse.json(),
    floorResponse ? floorResponse.json() : Promise.resolve(null),
    annotationResponse ? annotationResponse.json() : Promise.resolve(null),
    liveValidationResponse ? liveValidationResponse.json() : Promise.resolve(null),
    resourceBundleResponse ? resourceBundleResponse.json() : Promise.resolve(null),
  ]);
  if (annotationData && (annotationData.schema !== 'csgo2-radar/tactical-annotations/v1'
    || normalizeMapId(annotationData.mapId) !== normalized)) {
    throw new Error('CS2RADAR-MAP-207: tactical annotation boundary mismatch');
  }
  if (liveValidationData && (liveValidationData.schema !== 'pow-map-live-validation/v1'
    || normalizeMapId(liveValidationData.mapId) !== normalized
    || String(liveValidationData.buildId) !== String(nextManifest.liveValidation?.buildId))) {
    throw new Error('CS2RADAR-MAP-208: live-validation boundary mismatch');
  }
  if (resourceBundleData && (resourceBundleData.schema !== 'csgo2-radar/resource-bundle/v2'
    || normalizeMapId(resourceBundleData.mapId) !== normalized
    || String(resourceBundleData.sourceBuildId) !== String(nextManifest.sourceBuildId))) {
    throw new Error('CS2RADAR-MAP-210: resource-bundle boundary mismatch');
  }
  const demoUri = resourceBundleData?.artifacts?.demoScenarios?.uri;
  const grenadeUri = resourceBundleData?.artifacts?.grenades?.uri;
  const dynamicUri = resourceBundleData?.artifacts?.dynamicEntities?.uri;
  const [demoResponse, grenadeResponse, dynamicResponse] = await Promise.all([
    demoUri ? fetch(versionedResourceUrl(
      demoUri,
      base,
      resourceBundleData.artifacts.demoScenarios.sha256,
    ), { cache: 'force-cache' }) : Promise.resolve(null),
    grenadeUri ? fetch(versionedResourceUrl(
      grenadeUri,
      base,
      resourceBundleData.artifacts.grenades.sha256,
    ), { cache: 'force-cache' }) : Promise.resolve(null),
    dynamicUri ? fetch(versionedResourceUrl(
      dynamicUri,
      base,
      resourceBundleData.artifacts.dynamicEntities.sha256,
    ), { cache: 'force-cache' }) : Promise.resolve(null),
  ]);
  if ((demoResponse && !demoResponse.ok) || (grenadeResponse && !grenadeResponse.ok) || (dynamicResponse && !dynamicResponse.ok)) {
    throw new Error('CS2RADAR-MAP-211: default resource-bundle artifact request failed');
  }
  const [demoData, grenadeData, dynamicData] = await Promise.all([
    demoResponse ? demoResponse.json() : Promise.resolve(null),
    grenadeResponse ? grenadeResponse.json() : Promise.resolve(null),
    dynamicResponse ? dynamicResponse.json() : Promise.resolve(null),
  ]);
  if (demoData && (demoData.schema !== 'csgo2-radar/demo-scenarios/v1' || normalizeMapId(demoData.mapId) !== normalized)) {
    throw new Error('CS2RADAR-MAP-212: demo scenario boundary mismatch');
  }
  if (grenadeData && (grenadeData.schema !== 'csgo2-radar/grenade-index/v1' || normalizeMapId(grenadeData.mapId) !== normalized)) {
    throw new Error('CS2RADAR-MAP-213: grenade index boundary mismatch');
  }
  if (dynamicData && (dynamicData.schema !== 'cs2-dynamic-entities/v1' || normalizeMapId(dynamicData.map) !== normalized)) {
    throw new Error('CS2RADAR-MAP-214: dynamic entity boundary mismatch');
  }

  disposeCurrentMap();
  mapManifest = nextManifest;
  calibration = calibrationData;
  floorIndex = floorData ? validateFloorIndex(floorData) : null;
  tacticalAnnotations = annotationData;
  mapLiveValidation = liveValidationData;
  mapResourceBundle = resourceBundleData;
  mapDemoScenarios = demoData;
  mapGrenadeKnowledge = grenadeData;
  mapDynamicEntities = dynamicData;
  currentMapBase = base;
  radarImage = image;
  radarTexture = createRadarTexture(image);
  buyzoneImage = nextBuyzoneImage;
  buyzoneTexture = createSemanticTexture(nextBuyzoneImage);
  radarProjection = createRadarProjection(calibrationData, image, radarTexture);
  if (radarProjection) radarProjection.buyzoneTexture = buyzoneTexture;
  currentMapId = normalized;
  mapTriangleCount = 0;
  activeLayer = null;
  lastReferenceHeight = null;

  ui.mapSelect.value = normalized;
  ui.mapName.textContent = nextManifest.displayName;
  ui.mapCode.textContent = normalized;
  ui.brandSubtitle.textContent = `${nextManifest.displayName} · 2D / 3D 战术雷达`;
  populateLayerControls();
  setActiveLayer(availableLayers()[0], 'initial');
  populateDemoScenarioControls();

  const q = calibration.quality;
  const accepted = q.normalizedRmse <= q.acceptanceThreshold && q.liveAnchorCount >= 3;
  ui.calibrationState.textContent = accepted ? '已验证' : '待复核';
  const liveSamples = Number(nextManifest.liveValidation?.sampleCount || 0);
  ui.calibrationDetail.textContent = liveSamples > 0
    ? `Build ${nextManifest.liveValidation.buildId} · ${liveSamples} 个实机样本 · RMSE ${(q.normalizedRmse * 100).toFixed(3)}%`
    : `${q.liveAnchorCount} 个实测点 · RMSE ${(q.normalizedRmse * 100).toFixed(3)}%`;
  const artifacts = resourceBundleData?.artifacts || {};
  ui.resourceState.textContent = resourceBundleData ? '已接入' : '兼容模式';
  ui.resourceDetail.textContent = resourceBundleData
    ? `${artifacts.semantics?.nodes?.toLocaleString() || 0} 导航节点 · ${artifacts.grenades?.entryCount || 0} 投掷点 · ${artifacts.dynamicEntities?.entityCount || 0} 地图实体`
    : '当前地图未提供资源包 V2';
  ui.frameState.textContent = `${nextManifest.displayName} · 2D 已就绪 · Build ${nextManifest.sourceBuildId}`;
  if (demoMode) setStatus('ok', '离线演示', 'LOCAL');
  else if (liveSocket) setStatus('ok', 'CSGO2 已连接', 'LIVE');
  else setStatus('wait', '等待共享雷达连接', 'READY');
  draw2d();
  queueGeometryLoad();
}

async function loadMapPackage(mapId) {
  const normalized = normalizeMapId(mapId);
  if (currentMapId === normalized) {
    queueGeometryLoad();
    return;
  }
  if (loadingMapId === normalized && mapLoadPromise) return mapLoadPromise;
  if (mapLoadPromise) {
    await mapLoadPromise;
    return loadMapPackage(normalized);
  }
  loadingMapId = normalized;
  mapLoadPromise = performMapLoad(normalized);
  try {
    await mapLoadPromise;
  } finally {
    loadingMapId = null;
    mapLoadPromise = null;
  }
}

function fitMap() {
  if (!mapMesh) return;
  const geometryBox = new THREE.Box3().setFromObject(mapMesh);
  const calibratedBox = calibratedLayerBox(activeLayer, calibration);
  const box = calibratedBox ? calibratedBox.clone() : geometryBox;
  if (calibratedBox) {
    const geometryContext = activeLayer?.id === 'default' ? 0.22 : 0.04;
    box.min.lerp(geometryBox.min, geometryContext);
    box.max.lerp(geometryBox.max, geometryContext);
    // Far-below skybox/building geometry must not pull the active floor's camera down.
    box.min.y = calibratedBox.min.y;
    box.max.y = Math.min(calibratedBox.max.y, Number(ui.heightCut.value) * SOURCE_TO_METRES);
    box.max.y = Math.max(box.min.y + 0.1, box.max.y);
  }
  if (!calibratedBox && activeLayer) {
    const layerMin = Number(activeLayer.zMin) * SOURCE_TO_METRES;
    const layerMax = Math.min(Number(activeLayer.zMax), Number(ui.heightCut.value)) * SOURCE_TO_METRES;
    box.min.y = Math.max(box.min.y, layerMin);
    box.max.y = Math.min(box.max.y, Math.max(layerMin + 0.1, layerMax));
  }
  const center = box.getCenter(new THREE.Vector3());
  const size = box.getSize(new THREE.Vector3());
  const viewDirection = new THREE.Vector3(0.62, 1.12, 0.62).normalize();
  const distance = fitDistanceForBox(box, camera, viewDirection, calibratedBox ? 1.1 : 1.08, 18);
  controls.target.copy(center);
  camera.position.copy(center).addScaledVector(viewDirection, distance);
  controls.maxDistance = Math.max(260, distance * 3);
  camera.near = Math.max(0.05, distance / 400);
  camera.far = Math.max(500, distance + size.length() * 4);
  camera.updateProjectionMatrix();
  controls.update();
}

function playerColor(player) {
  if (player.local) return 0x35e08d;
  return player.team === 3 ? 0x36a9ff : 0xff9b45;
}

function normalizedWeaponAssetId(weapon) {
  const normalized = String(weapon || '').toLowerCase();
  if (normalized.includes('ak')) return 'ak47';
  if (normalized.includes('m4')) return 'm4a4';
  return normalized || 'unknown';
}

function playerProfileKey(player) {
  return [
    Number(player.team),
    player.local ? 'local' : 'remote',
    normalizedWeaponAssetId(player.weapon),
    operatorGeometryState,
    officialWeaponState,
  ].join(':');
}

function addPlayerMarker(player) {
  const root = new THREE.Group();
  const color = playerColor(player);
  const direction = new THREE.Mesh(
    markerGeometry.direction,
    markerMaterial(color, 'basic'),
  );
  direction.rotation.x = Math.PI / 2;
  direction.position.set(0, 0.08, -0.72);
  const healthColor = player.health > 65 ? 0x35e08d : player.health > 30 ? 0xf4b94f : 0xff4e64;
  const health = new THREE.Mesh(markerGeometry.health, markerMaterial(healthColor, 'basic'));
  health.rotation.x = Math.PI / 2;
  health.position.y = 0.06;
  health.scale.setScalar(THREE.MathUtils.clamp(Number(player.health) / 100, 0.18, 1));
  const teamFallbackFamily = player.team === 2 ? 'tm_phoenix' : 'ctm_sas';
  const oppositeFallbackFamily = player.team === 2 ? 'ctm_sas' : 'tm_phoenix';
  const operatorGeometry = operatorGeometries.get(player.modelFamily || teamFallbackFamily)
    || operatorGeometries.get(teamFallbackFamily)
    || operatorGeometries.get(oppositeFallbackFamily);
  if (operatorGeometry) {
    const operator = createPowOperatorSkinnedMesh(operatorGeometry, operatorMaterial(player.team, player.local));
    operator.rotation.y = Math.PI / 2;
    operator.renderOrder = 20;
    attachOperatorEquipment(operator, player);
    // Clear only the depth buffer before each operator. The model stays visible
    // through the map while its own triangles depth-test normally. A scaled
    // back-face outline is intentionally avoided here: the official SAS model
    // consists of many overlapping clothing parts and that technique exposes
    // dark fragments between them. A screen-space edge pass can be added later.
    operator.onBeforeRender = (activeRenderer) => activeRenderer.clearDepth();
    root.add(operator, direction, health);
    root.userData.operator = operator;
    root.userData.baseScale = 1;
  } else {
    const bodyMaterial = markerMaterial(color);
    const body = new THREE.Mesh(markerGeometry.body, bodyMaterial);
    body.position.y = 0.72;
    const head = new THREE.Mesh(markerGeometry.head, bodyMaterial);
    head.position.y = 1.55;
    root.add(body, head, direction, health);
    root.userData.baseScale = 1.8;
  }
  root.traverse((object) => { object.renderOrder ||= 20; });
  root.userData = {
    ...root.userData,
    kind: 'player',
    player,
    profileKey: playerProfileKey(player),
    directionIndicator: direction,
    healthRing: health,
    lastAngle: Number(player.angle) || 0,
    lastSnapshotAt: performance.now(),
    nextAnimationUpdateAt: 0,
    transformInitialized: false,
    interpolationFrom: new THREE.Vector3(),
    interpolationTarget: new THREE.Vector3(),
    interpolationStartedAt: 0,
    interpolationDurationMs: 100,
    yawFrom: 0,
    yawTarget: 0,
  };
  playerEntityRoot.add(root);
  playerMarkerById.set(String(player.id), root);
  updatePlayerMarker(root, player, performance.now());
  return root;
}

function updatePlayerMarker(root, player, now) {
  const nextPosition = sourceToThree(player.position);
  const nextAngle = Number(player.angle) || 0;
  const elapsedSeconds = Math.max(0.001, (now - Number(root.userData.lastSnapshotAt || now)) / 1000);
  const wrappedDelta = ((nextAngle - Number(root.userData.lastAngle || 0) + 540) % 360) - 180;
  const turnRateDegreesPerSecond = wrappedDelta / elapsedSeconds;
  const nextYaw = THREE.MathUtils.degToRad(nextAngle);
  if (!root.userData.transformInitialized
    || now - Number(root.userData.lastSnapshotAt || now) > 450
    || root.position.distanceTo(nextPosition) > 4.5) {
    root.position.copy(nextPosition);
    root.rotation.y = nextYaw;
    root.userData.interpolationFrom.copy(nextPosition);
    root.userData.interpolationTarget.copy(nextPosition);
    root.userData.yawFrom = nextYaw;
    root.userData.yawTarget = nextYaw;
    root.userData.transformInitialized = true;
  } else {
    root.userData.interpolationFrom.copy(root.position);
    root.userData.interpolationTarget.copy(nextPosition);
    root.userData.interpolationStartedAt = now;
    root.userData.interpolationDurationMs = THREE.MathUtils.clamp(elapsedSeconds * 1000 * 1.08, 72, 150);
    root.userData.yawFrom = root.rotation.y;
    root.userData.yawTarget = root.rotation.y + Math.atan2(
      Math.sin(nextYaw - root.rotation.y),
      Math.cos(nextYaw - root.rotation.y),
    );
  }
  const layerScale = activeLayerOpacity(player.position.z) < 1 ? 0.72 : 1;
  root.scale.setScalar(Number(root.userData.baseScale || 1) * layerScale);
  root.visible = focusedPlayerId === null || focusedPlayerId === String(player.id);
  if (root.userData.directionIndicator) root.userData.directionIndicator.visible = focusedPlayerId === null;
  if (root.userData.healthRing) root.userData.healthRing.visible = focusedPlayerId === null;
  const healthColor = player.health > 65 ? 0x35e08d : player.health > 30 ? 0xf4b94f : 0xff4e64;
  if (root.userData.healthRing) {
    root.userData.healthRing.material = markerMaterial(healthColor, 'basic');
    root.userData.healthRing.scale.setScalar(THREE.MathUtils.clamp(Number(player.health) / 100, 0.18, 1));
  }
  const operator = root.userData.operator;
  if (operator) {
    operator.userData.animationSnapshot = {
      ...(player.animation || { action: 'hold', locomotionCycle: 0, speedUnitsPerSecond: 0 }),
      turnRateDegreesPerSecond,
      updatedAt: now,
    };
  }
  root.userData.player = player;
  root.userData.lastAngle = nextAngle;
  root.userData.lastSnapshotAt = now;
}

function updatePlayerTransforms(time) {
  let focusedRoot = null;
  playerEntityRoot.children.forEach((root) => {
    if (!root.userData?.transformInitialized) return;
    const duration = Math.max(1, Number(root.userData.interpolationDurationMs || 100));
    const progress = THREE.MathUtils.clamp(
      (time - Number(root.userData.interpolationStartedAt || time)) / duration,
      0,
      1,
    );
    const eased = progress * progress * (3 - 2 * progress);
    root.position.lerpVectors(root.userData.interpolationFrom, root.userData.interpolationTarget, eased);
    root.rotation.y = THREE.MathUtils.lerp(root.userData.yawFrom, root.userData.yawTarget, eased);
    if (focusedPlayerId === String(root.userData.player?.id)) focusedRoot = root;
  });
  if (focusedRoot) {
    focusedCameraTarget.copy(focusedRoot.position);
    focusedCameraTarget.y += 0.92;
    focusedCameraOffset.copy(focusedCameraTarget).sub(controls.target);
    controls.target.add(focusedCameraOffset);
    camera.position.add(focusedCameraOffset);
  }
}

function addPoint(position, color, scale = 1) {
  const mesh = new THREE.Mesh(markerGeometry.point, markerMaterial(color));
  mesh.position.copy(sourceToThree(position));
  mesh.position.y += 0.25;
  mesh.scale.setScalar(scale);
  mesh.renderOrder = 21;
  transientEntityRoot.add(mesh);
}

function renderCombatBursts(frame) {
  const elapsedMs = Number(frame.demo?.elapsedMs || 0);
  const playerById = new Map(frame.players.map((player) => [String(player.id), player]));
  const events = (frame.demo?.recentCombatEvents || []).filter((event) => (
    ['damage', 'kill'].includes(event.type)
    && elapsedMs >= Number(event.timeMs)
    && elapsedMs - Number(event.timeMs) <= 700
  ));
  lastCombatBurstCount = events.length;
  events.forEach((event) => {
    const attacker = playerById.get(String(event.actorId));
    const victim = playerById.get(String(event.victimId));
    if (!attacker || !victim) return;
    const from = sourceToThree(attacker.position);
    const to = sourceToThree(victim.position);
    from.y += 1.2;
    to.y += 1.05;
    const delta = to.clone().sub(from);
    const length = delta.length();
    if (length <= 0.01) return;
    const material = event.type === 'kill' ? combatMaterials.kill : combatMaterials.damage;
    const beam = new THREE.Mesh(combatGeometry.beam, material);
    beam.position.copy(from).add(to).multiplyScalar(0.5);
    beam.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), delta.normalize());
    beam.scale.set(1, length, 1);
    beam.renderOrder = 90;
    const impact = new THREE.Mesh(combatGeometry.impact, material);
    impact.position.copy(to);
    impact.quaternion.copy(camera.quaternion);
    impact.scale.setScalar(event.type === 'kill' ? 1.8 : 1.2);
    impact.renderOrder = 91;
    transientEntityRoot.add(beam, impact);
  });
}

function directCameraToDemoEvent(frame) {
  if (!demoDirectorEnabled || focusedPlayerId !== null || currentView !== '3d') return;
  const event = frame.demo?.activeEvent;
  if (!event || event.id === lastDirectedEventId) return;
  lastDirectedEventId = event.id;
  const playerById = new Map(frame.players.map((player) => [String(player.id), player]));
  const subjects = [event.actorId, event.victimId]
    .map((id) => playerById.get(String(id || '')))
    .filter(Boolean);
  if (!subjects.length) return;
  const points = subjects.map((player) => sourceToThree(player.position));
  directorCameraTarget.copy(points.reduce((sum, point) => sum.add(point), new THREE.Vector3()).multiplyScalar(1 / points.length));
  directorCameraTarget.y += 0.9;
  const span = points.length > 1 ? points[0].distanceTo(points[1]) : 3;
  const distance = THREE.MathUtils.clamp(span * 1.25 + 7, 8, 28);
  directorCameraPosition.copy(directorCameraTarget).add(new THREE.Vector3(distance * 0.72, distance * 0.62, distance * 0.72));
  directorTransitionActive = true;
}

function updateDirectorCameraTransition() {
  if (!directorTransitionActive || focusedPlayerId !== null || !demoDirectorEnabled) return;
  controls.target.lerp(directorCameraTarget, 0.075);
  camera.position.lerp(directorCameraPosition, 0.075);
  if (controls.target.distanceTo(directorCameraTarget) < 0.04
    && camera.position.distanceTo(directorCameraPosition) < 0.08) {
    controls.target.copy(directorCameraTarget);
    camera.position.copy(directorCameraPosition);
    directorTransitionActive = false;
  }
}

function projectileAssetId(type) {
  if (type === 'smoke') return 'smokegrenade';
  if (type === 'flashbang') return 'flashbang';
  if (type === 'hegrenade') return 'hegrenade';
  return null;
}

function seededProjectileValue(seed, index) {
  const value = Math.sin((seed + 1) * 91.731 + (index + 1) * 37.117) * 43758.5453;
  return value - Math.floor(value);
}

const projectileVelocityDirection = new THREE.Vector3();
const projectileTrailAxis = new THREE.Vector3(0, 1, 0);

function createProjectileEffect(item) {
  const root = new THREE.Group();
  root.name = `projectile-${item.id}`;
  const ownedMaterials = [];
  const assetId = projectileAssetId(item.type);
  const flightProp = assetId ? createOfficialAsset(assetId) : null;
  if (flightProp) {
    flightProp.scale.multiplyScalar(1.08);
    root.add(flightProp);
  } else {
    const material = new THREE.MeshStandardMaterial({ color: 0x6b7650, roughness: 0.65, metalness: 0.25 });
    ownedMaterials.push(material);
    root.add(new THREE.Mesh(projectileGeometry.fallback, material));
  }
  const trailMaterial = new THREE.MeshBasicMaterial({
    color: item.type === 'flashbang' ? 0xdff7ff : item.type === 'smoke' ? 0x9ca8ae : 0xffa24a,
    transparent: true,
    opacity: 0,
    depthWrite: false,
    depthTest: false,
    blending: THREE.AdditiveBlending,
  });
  ownedMaterials.push(trailMaterial);
  const flightTrail = new THREE.Mesh(projectileGeometry.trail, trailMaterial);
  flightTrail.name = 'flight-trail';
  flightTrail.renderOrder = 79;
  root.add(flightTrail);
  const cueColor = item.type === 'flashbang'
    ? 0xdff7ff
    : item.type === 'smoke'
      ? 0xaebbc2
      : item.type === 'inferno'
        ? 0xff6a28
        : 0xffa24a;
  const flightCueMaterial = new THREE.MeshBasicMaterial({
    color: cueColor,
    transparent: true,
    opacity: 0,
    depthWrite: false,
    depthTest: false,
    side: THREE.DoubleSide,
    blending: THREE.AdditiveBlending,
  });
  ownedMaterials.push(flightCueMaterial);
  const flightCue = new THREE.Mesh(projectileGeometry.cue, flightCueMaterial);
  flightCue.name = 'flight-cue';
  flightCue.renderOrder = 80;
  flightCue.position.y = 0.18;
  root.add(flightCue);
  const visual = new THREE.Group();
  visual.name = 'detonation-effect';
  visual.visible = false;
  root.add(visual);
  const seed = [...String(item.id)].reduce((sum, char) => sum + char.charCodeAt(0), 0);

  if (item.type === 'smoke') {
    const smokeMaterial = new THREE.MeshBasicMaterial({
      color: 0x87939b,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      depthTest: false,
    });
    ownedMaterials.push(smokeMaterial);
    for (let index = 0; index < 26; index += 1) {
      const puff = new THREE.Mesh(projectileGeometry.smoke, smokeMaterial);
      const angle = seededProjectileValue(seed, index * 3) * Math.PI * 2;
      const radial = index < 6
        ? 0.03 + seededProjectileValue(seed, index * 3 + 1) * 0.18
        : 0.16 + seededProjectileValue(seed, index * 3 + 1) * 0.84;
      const height = 0.12 + seededProjectileValue(seed, index * 3 + 2) * 0.78;
      puff.userData.effectOffset = new THREE.Vector3(Math.cos(angle) * radial, height, Math.sin(angle) * radial);
      puff.userData.effectScale = 0.92 + seededProjectileValue(seed, index + 80) * 0.88;
      puff.renderOrder = 82;
      visual.add(puff);
    }
    const smokeAreaMaterial = new THREE.MeshBasicMaterial({
      color: 0x9ca8ae,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      depthTest: false,
      side: THREE.DoubleSide,
    });
    const smokeRingMaterial = smokeAreaMaterial.clone();
    ownedMaterials.push(smokeAreaMaterial, smokeRingMaterial);
    const smokeArea = new THREE.Mesh(projectileGeometry.area, smokeAreaMaterial);
    const smokeRing = new THREE.Mesh(projectileGeometry.areaRing, smokeRingMaterial);
    smokeArea.rotation.x = -Math.PI / 2;
    smokeRing.rotation.x = -Math.PI / 2;
    smokeArea.position.y = 0.035;
    smokeRing.position.y = 0.045;
    smokeArea.renderOrder = 80;
    smokeRing.renderOrder = 81;
    visual.add(smokeArea, smokeRing);
    root.userData.smokeMaterial = smokeMaterial;
    root.userData.smokeAreaMaterial = smokeAreaMaterial;
    root.userData.smokeRingMaterial = smokeRingMaterial;
    root.userData.smokeArea = smokeArea;
    root.userData.smokeRing = smokeRing;
  } else if (item.type === 'inferno') {
    const fireMaterial = new THREE.MeshBasicMaterial({
      color: 0xff6a28,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      depthTest: false,
      blending: THREE.AdditiveBlending,
    });
    ownedMaterials.push(fireMaterial);
    for (let index = 0; index < 14; index += 1) {
      const flame = new THREE.Mesh(projectileGeometry.spark, fireMaterial);
      const angle = seededProjectileValue(seed, index) * Math.PI * 2;
      const radial = 0.2 + seededProjectileValue(seed, index + 30) * 0.8;
      flame.userData.effectOffset = new THREE.Vector3(Math.cos(angle) * radial, 0, Math.sin(angle) * radial);
      flame.userData.phase = seededProjectileValue(seed, index + 60) * Math.PI * 2;
      visual.add(flame);
    }
    root.userData.fireMaterial = fireMaterial;
  } else {
    const flash = item.type === 'flashbang';
    const coreMaterial = new THREE.MeshBasicMaterial({
      color: flash ? 0xffffff : 0xffb13b,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      depthTest: false,
      blending: THREE.AdditiveBlending,
    });
    const ringMaterial = new THREE.MeshBasicMaterial({
      color: flash ? 0xdff7ff : 0xff6a28,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      depthTest: false,
      blending: THREE.AdditiveBlending,
    });
    ownedMaterials.push(coreMaterial, ringMaterial);
    const core = new THREE.Mesh(projectileGeometry.flash, coreMaterial);
    const ringA = new THREE.Mesh(projectileGeometry.shockwave, ringMaterial);
    const ringB = new THREE.Mesh(projectileGeometry.shockwave, ringMaterial);
    ringA.rotation.x = Math.PI / 2;
    ringB.rotation.y = Math.PI / 2;
    visual.add(core, ringA, ringB);
    root.userData.effectCore = core;
    root.userData.effectRings = [ringA, ringB];
    root.userData.coreMaterial = coreMaterial;
    root.userData.ringMaterial = ringMaterial;
    if (!flash) {
      const sparkMaterial = ringMaterial.clone();
      ownedMaterials.push(sparkMaterial);
      for (let index = 0; index < 18; index += 1) {
        const spark = new THREE.Mesh(projectileGeometry.spark, sparkMaterial);
        const azimuth = seededProjectileValue(seed, index * 2) * Math.PI * 2;
        const elevation = (seededProjectileValue(seed, index * 2 + 1) - 0.22) * Math.PI * 0.62;
        spark.userData.effectDirection = new THREE.Vector3(
          Math.cos(azimuth) * Math.cos(elevation),
          Math.sin(elevation),
          Math.sin(azimuth) * Math.cos(elevation),
        ).normalize();
        visual.add(spark);
      }
      root.userData.sparkMaterial = sparkMaterial;
    }
    const light = new THREE.PointLight(flash ? 0xe9fbff : 0xff7a32, 0, flash ? 16 : 10);
    visual.add(light);
    root.userData.effectLight = light;
  }

  root.userData.flightProp = flightProp || root.children[0];
  root.userData.flightTrail = flightTrail;
  root.userData.flightTrailMaterial = trailMaterial;
  root.userData.flightCue = flightCue;
  root.userData.flightCueMaterial = flightCueMaterial;
  root.userData.effectVisual = visual;
  root.userData.animationSeed = seed;
  root.userData.ownedMaterials = ownedMaterials;
  root.userData.profileKey = `${item.type}:${officialWeaponState}`;
  root.userData.interpolationFrom = new THREE.Vector3();
  root.userData.interpolationTarget = sourceToThree(item.position);
  root.userData.interpolationStartedAt = performance.now();
  root.userData.interpolationDurationMs = 100;
  root.position.copy(root.userData.interpolationTarget);
  projectileEntityRoot.add(root);
  projectileEffectById.set(String(item.id), root);
  return root;
}

function removeProjectileEffect(id, root) {
  projectileEntityRoot.remove(root);
  root.userData.ownedMaterials?.forEach((material) => material.dispose());
  projectileEffectById.delete(String(id));
}

function syncProjectileEffects(projectiles, now) {
  const liveIds = new Set(projectiles.map((item) => String(item.id)));
  for (const [id, root] of projectileEffectById) {
    if (!liveIds.has(id)) removeProjectileEffect(id, root);
  }
  projectiles.forEach((item) => {
    const id = String(item.id);
    const expectedProfile = `${item.type}:${officialWeaponState}`;
    let root = projectileEffectById.get(id);
    if (root && root.userData.profileKey !== expectedProfile) {
      removeProjectileEffect(id, root);
      root = null;
    }
    if (!root) root = createProjectileEffect(item);
    root.userData.interpolationFrom.copy(root.position);
    root.userData.interpolationTarget.copy(sourceToThree(item.position));
    root.userData.interpolationStartedAt = now;
    root.userData.interpolationDurationMs = 110;
    root.userData.snapshot = item;
    root.userData.snapshotUpdatedAt = now;
  });
}

function resolvedProjectileEffectAge(snapshot, time, snapshotUpdatedAt) {
  const snapshotAge = Math.max(0, Number(snapshot?.effectAgeMs || 0));
  if (demoMode && demoPaused) return snapshotAge;
  return snapshotAge + Math.max(0, time - Number(snapshotUpdatedAt || time));
}

function updateProjectileEffects(time) {
  projectileEffectById.forEach((root) => {
    const snapshot = root.userData.snapshot;
    if (!snapshot) return;
    const movementProgress = THREE.MathUtils.clamp(
      (time - Number(root.userData.interpolationStartedAt || time))
        / Math.max(1, Number(root.userData.interpolationDurationMs || 100)),
      0,
      1,
    );
    const movementEase = movementProgress * movementProgress * (3 - 2 * movementProgress);
    root.position.lerpVectors(root.userData.interpolationFrom, root.userData.interpolationTarget, movementEase);
    root.position.y += 0.14;
    const inFlight = snapshot.state !== 'detonated' && !snapshot.exploded;
    root.userData.flightProp.visible = inFlight;
    root.userData.flightTrail.visible = inFlight;
    root.userData.flightCue.visible = inFlight;
    root.userData.effectVisual.visible = !inFlight;
    if (inFlight) {
      const flightSeconds = (time - Number(root.userData.snapshotUpdatedAt || time)) / 1000;
      projectileVelocityDirection.set(
        Number(snapshot.velocity?.x) || 0,
        Number(snapshot.velocity?.z) || 0,
        -(Number(snapshot.velocity?.y) || 0),
      );
      const sourceSpeed = projectileVelocityDirection.length();
      if (sourceSpeed > 1) {
        projectileVelocityDirection.normalize();
        const trailLength = THREE.MathUtils.clamp(sourceSpeed * SOURCE_TO_METRES * 0.08, 0.35, 1.45);
        root.userData.flightTrail.quaternion.setFromUnitVectors(projectileTrailAxis, projectileVelocityDirection);
        root.userData.flightTrail.scale.set(1, trailLength, 1);
        root.userData.flightTrail.position.copy(projectileVelocityDirection).multiplyScalar(-trailLength * 0.5);
        root.userData.flightTrailMaterial.opacity = 0.18 + Math.sin(time * 0.012) * 0.035;
      } else {
        root.userData.flightTrail.visible = false;
      }
      root.userData.flightProp.rotation.x += 0.075;
      root.userData.flightProp.rotation.z += 0.11;
      root.position.y += Math.sin((time + Number(root.userData.animationSeed || 0)) * 0.016) * 0.004;
      if (flightSeconds > 0.12) root.userData.flightProp.rotation.y += 0.035;
      const flightCueScale = THREE.MathUtils.clamp(camera.position.distanceTo(root.position) / 55, 1, 5);
      root.userData.flightCue.quaternion.copy(camera.quaternion);
      root.userData.flightCue.scale.setScalar(flightCueScale * (0.92 + Math.sin(time * 0.01) * 0.08));
      root.userData.flightCueMaterial.opacity = 0.62 + Math.sin(time * 0.012) * 0.14;
      root.userData.flightCueScale = flightCueScale;
      return;
    }
    root.userData.flightTrailMaterial.opacity = 0;
    root.userData.flightCueMaterial.opacity = 0;
    const age = resolvedProjectileEffectAge(snapshot, time, root.userData.snapshotUpdatedAt);
    root.userData.resolvedEffectAgeMs = age;
    const duration = Math.max(1, Number(snapshot.effectDurationMs || 1000));
    const progress = THREE.MathUtils.clamp(age / duration, 0, 1);
    const radius = Math.max(1.8, Number(snapshot.effectRadius || 280) * SOURCE_TO_METRES);
    const tacticalVisibilityScale = THREE.MathUtils.clamp(camera.position.distanceTo(root.position) / 78, 1, 2.4);
    root.userData.effectVisual.scale.setScalar(tacticalVisibilityScale);

    if (snapshot.type === 'smoke') {
      const expansion = THREE.MathUtils.smoothstep(Math.min(age / 2200, 1), 0, 1);
      const fade = progress > 0.82 ? 1 - THREE.MathUtils.smoothstep(progress, 0.82, 1) : 1;
      root.userData.smokeMaterial.opacity = (0.16 + expansion * 0.22) * fade;
      root.userData.effectVisual.children.forEach((puff, index) => {
        if (!puff.userData.effectOffset) return;
        puff.position.copy(puff.userData.effectOffset).multiplyScalar(radius * (0.18 + expansion * 0.82));
        puff.position.y += Math.sin(time * 0.00075 + index) * 0.12;
        const pulse = 0.92 + Math.sin(time * 0.0011 + index * 1.7) * 0.08;
        puff.scale.setScalar(puff.userData.effectScale * (0.62 + expansion * 1.16) * pulse);
        puff.rotation.y += 0.0025 * (index % 2 ? 1 : -1);
      });
      const areaScale = radius * (0.48 + expansion * 0.52);
      root.userData.smokeArea.scale.setScalar(areaScale);
      root.userData.smokeRing.scale.setScalar(areaScale);
      root.userData.smokeAreaMaterial.opacity = (0.05 + expansion * 0.07) * fade;
      root.userData.smokeRingMaterial.opacity = (0.16 + expansion * 0.12) * fade;
      return;
    }
    if (snapshot.type === 'inferno') {
      const fade = progress > 0.76 ? 1 - THREE.MathUtils.smoothstep(progress, 0.76, 1) : 1;
      root.userData.fireMaterial.opacity = 0.68 * fade;
      root.userData.effectVisual.children.forEach((flame, index) => {
        if (!flame.userData.effectOffset) return;
        flame.position.copy(flame.userData.effectOffset).multiplyScalar(radius * 0.72);
        flame.position.y = 0.18 + Math.abs(Math.sin(time * 0.009 + flame.userData.phase)) * 1.4;
        flame.scale.setScalar(1.5 + Math.sin(time * 0.011 + index) * 0.45);
      });
      return;
    }

    const flash = snapshot.type === 'flashbang';
    const burst = flash ? Math.min(1, age / 110) : Math.min(1, age / 180);
    const decay = (1 - progress) ** (flash ? 3.2 : 2.1);
    root.userData.coreMaterial.opacity = decay * (flash ? 0.96 : 0.82);
    root.userData.ringMaterial.opacity = decay * (flash ? 0.72 : 0.62);
    root.userData.effectCore.scale.setScalar((0.18 + burst * radius * (flash ? 0.48 : 0.32)) / 0.23);
    root.userData.effectRings.forEach((ring, index) => {
      ring.scale.setScalar(0.2 + radius * (0.20 + progress * (flash ? 1.08 : 0.86)));
      ring.rotation.z += 0.012 * (index ? -1 : 1);
    });
    if (root.userData.sparkMaterial) {
      root.userData.sparkMaterial.opacity = decay * 0.9;
      root.userData.effectVisual.children.forEach((spark) => {
        if (!spark.userData.effectDirection) return;
        spark.position.copy(spark.userData.effectDirection).multiplyScalar(radius * THREE.MathUtils.smoothstep(progress, 0, 0.82));
        spark.position.y -= progress * progress * 1.8;
        spark.scale.setScalar(1.2 + decay * 2.2);
      });
    }
    root.userData.effectLight.intensity = decay * (flash ? 12 : 8);
  });
  if (runtimeDebugEnabled) {
    camera.updateMatrixWorld();
    globalThis.__CS2_RADAR_DEBUG__ = {
      camera: {
        position: camera.position.toArray(),
        target: controls.target.toArray(),
        near: camera.near,
        far: camera.far,
      },
      rendering: {
        quality: ui.renderQuality.value,
        style: ui.renderStyle.value,
        pixelRatio: renderer.getPixelRatio(),
        canvas: [renderer.domElement.width, renderer.domElement.height],
        composer: [composer.readBuffer.width, composer.readBuffer.height],
        ssao: [ssaoPass.normalRenderTarget.width, ssaoPass.normalRenderTarget.height],
        fxaa: fxaaPass.enabled,
        fxaaTexel: fxaaPass.material.uniforms.resolution.value.toArray(),
        ssaoEnabled: ssaoPass.enabled,
        ssaoCameraNear: ssaoPass.ssaoMaterial.uniforms.cameraNear.value,
        ssaoCameraFar: ssaoPass.ssaoMaterial.uniforms.cameraFar.value,
        gridVisible: grid.visible,
        edgesVisible: Boolean(mapEdges?.visible),
      },
      map: {
        id: currentMapId,
        triangleCount: mapTriangleCount,
        surfaces: (mapSurfaceData?.surfaceNames || []).map((name, index) => ({
          index,
          name,
          visible: Array.isArray(mapMesh?.material) ? mapMesh.material[index]?.visible !== false : true,
        })),
        materialRuns: mapSurfaceData?.groups.length || 0,
        activeLayer: activeLayer?.id,
        cropEnabled: shouldCropToLayer(activeLayer),
        clippingPlaneCount: (Array.isArray(mapMesh?.material)
          ? mapMesh.material[0] : mapMesh?.material)?.clippingPlanes?.length || 0,
      },
      demo: {
        playbackRate: demoPlaybackRate,
        directorEnabled: demoDirectorEnabled,
        directorTransitionActive,
        activeEventId: currentFrame?.demo?.activeEvent?.id || null,
        combatBurstCount: lastCombatBurstCount,
        objectiveState: currentFrame?.demo?.objectiveState || null,
        roundFinished: Boolean(currentFrame?.demo?.roundFinished),
        winnerTeam: Number(currentFrame?.demo?.winnerTeam || 0),
      },
      projectiles: [...projectileEffectById.entries()].map(([id, root]) => {
        const world = root.getWorldPosition(new THREE.Vector3());
        const projected = world.clone().project(camera);
        return {
          id,
          type: root.userData.snapshot?.type,
          state: root.userData.snapshot?.state,
          effectAgeMs: root.userData.snapshot?.effectAgeMs,
          resolvedEffectAgeMs: root.userData.resolvedEffectAgeMs,
          sourcePosition: root.userData.snapshot?.position,
          localPosition: root.position.toArray(),
          interpolationFrom: root.userData.interpolationFrom?.toArray(),
          interpolationTarget: root.userData.interpolationTarget?.toArray(),
          interpolationStartedAt: root.userData.interpolationStartedAt,
          world: world.toArray(),
          ndc: projected.toArray(),
          screen: [
            (projected.x * 0.5 + 0.5) * renderer.domElement.clientWidth,
            (-projected.y * 0.5 + 0.5) * renderer.domElement.clientHeight,
          ],
          insideClip: Math.abs(projected.x) <= 1 && Math.abs(projected.y) <= 1 && projected.z >= -1 && projected.z <= 1,
          rootVisible: root.visible && projectileEntityRoot.visible,
          effectVisible: root.userData.effectVisual?.visible,
          effectScale: root.userData.effectVisual?.scale.x,
          smokeOpacity: root.userData.smokeMaterial?.opacity,
          flightTrailVisible: root.userData.flightTrail?.visible,
          flightTrailOpacity: root.userData.flightTrailMaterial?.opacity,
          flightTrailScale: root.userData.flightTrail?.scale.toArray(),
          flightTrailPosition: root.userData.flightTrail?.position.toArray(),
          flightCueVisible: root.userData.flightCue?.visible,
          flightCueScale: root.userData.flightCueScale,
          flightCueOpacity: root.userData.flightCueMaterial?.opacity,
          velocity: root.userData.snapshot?.velocity,
          childCount: root.userData.effectVisual?.children.length,
        };
      }),
    };
  }
}

function renderEntities(frame) {
  const now = performance.now();
  transientEntityRoot.clear();
  const visiblePlayers = frame.players.filter((p) => (demoMode && p.animation?.action === 'death') || ui.showDead.checked || !p.dead);
  const visibleIds = new Set(visiblePlayers.map((player) => String(player.id)));
  for (const [id, root] of playerMarkerById) {
    if (visibleIds.has(id)) continue;
    playerEntityRoot.remove(root);
    playerMarkerById.delete(id);
  }
  visiblePlayers.forEach((player) => {
    const id = String(player.id);
    let root = playerMarkerById.get(id);
    if (root && root.userData.profileKey !== playerProfileKey(player)) {
      playerEntityRoot.remove(root);
      playerMarkerById.delete(id);
      root = null;
    }
    if (!root) root = addPlayerMarker(player);
    else updatePlayerMarker(root, player, now);
  });
  transientEntityRoot.visible = focusedPlayerId === null;
  projectileEntityRoot.visible = focusedPlayerId === null;
  if (frame.bomb && !frame.bomb.defused) addPoint(frame.bomb.position, 0xff3557, 1.35);
  renderCombatBursts(frame);
  syncProjectileEffects(frame.projectiles, now);
  frame.droppedWeapons.forEach((item) => addPoint(item.position, 0xb982ff, 0.55));
  ui.entityCount.textContent = String(visiblePlayers.length + frame.projectiles.length + frame.droppedWeapons.length + (frame.bomb ? 1 : 0));
  const rosterSignature = visiblePlayers.map((player) => [
    player.id,
    player.name,
    player.team,
    player.weapon,
    player.health,
    player.dead ? 1 : 0,
    player.animation?.action || 'hold',
    Math.round(Number(player.animation?.speedUnitsPerSecond || 0) / 5),
  ].join(':')).join('|');
  visiblePlayers.forEach((player) => {
    const row = rosterRowById.get(String(player.id));
    if (row) row.playerSnapshot = player;
  });
  if (rosterSignature !== lastRosterSignature) {
    lastRosterSignature = rosterSignature;
    renderRoster(visiblePlayers);
  }
  updateFocusHud(visiblePlayers.find((player) => String(player.id) === focusedPlayerId) || null);
  renderCombatFeed(frame.demo?.recentCombatEvents || []);
  directCameraToDemoEvent(frame);
  operatorOutlinePass.selectedObjects = visiblePlayers
    .map((player) => playerMarkerById.get(String(player.id))?.userData?.operator)
    .filter(Boolean);
}

function focusPlayerModel(player) {
  if (!player?.position) return;
  focusedPlayerId = String(player.id);
  playerEntityRoot.children.forEach((object) => {
    const selected = object.userData?.kind === 'player'
      && String(object.userData?.player?.id) === String(player.id);
    object.visible = selected;
    if (object.userData.directionIndicator) object.userData.directionIndicator.visible = !selected;
    if (object.userData.healthRing) object.userData.healthRing.visible = !selected;
  });
  transientEntityRoot.visible = false;
  projectileEntityRoot.visible = false;
  annotationRoot.visible = false;
  const target = sourceToThree(player.position);
  target.y += 0.92;
  controls.target.copy(target);
  camera.position.copy(target).add(new THREE.Vector3(2.8, 1.65, 2.8));
  camera.near = 0.025;
  camera.far = Math.max(camera.far, 500);
  camera.updateProjectionMatrix();
  controls.update();
  updateFocusHud(player);
}

function renderRoster(players) {
  const liveIds = new Set(players.map((player) => String(player.id)));
  for (const [id, row] of rosterRowById) {
    if (liveIds.has(id)) continue;
    row.remove();
    rosterRowById.delete(id);
  }
  players.forEach((player) => {
    const id = String(player.id);
    let row = rosterRowById.get(id);
    if (!row) {
      row = document.createElement('div');
      row.tabIndex = 0;
      row.role = 'button';
      row.title = '点击近景查看人物与持枪动作';
      row.innerHTML = '<i></i><div><strong></strong><small></small></div><b></b>';
      row.addEventListener('click', () => focusPlayerModel(row.playerSnapshot));
      row.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          focusPlayerModel(row.playerSnapshot);
        }
      });
      rosterRowById.set(id, row);
    }
    row.playerSnapshot = player;
    row.className = `player ${player.team === 2 ? 'team-t' : ''} ${player.local ? 'is-local' : ''} ${player.dead ? 'is-dead' : ''}`;
    const side = player.team === 3 ? 'CT' : 'T';
    row.querySelector('strong').textContent = player.name;
    const action = player.animation?.action ? ` · ${PLAYER_ACTION_LABELS[player.animation.action] || player.animation.action}` : '';
    const speed = ['walk', 'run'].includes(player.animation?.action) && Number(player.animation?.speedUnitsPerSecond) > 0
      ? ` · ${Math.round(Number(player.animation.speedUnitsPerSecond))} u/s`
      : '';
    row.querySelector('small').textContent = `${side} · ${player.weapon || '未读取武器'}${action}${speed}`;
    row.querySelector('b').textContent = player.dead ? '阵亡' : `${Math.max(0, player.health)} HP`;
    ui.playerList.append(row);
  });
}

function updateFocusHud(player) {
  if (!focusedPlayerId || !player) {
    ui.focusHud?.classList.add('hidden');
    return;
  }
  const side = player.team === 3 ? 'CT' : 'T';
  const action = PLAYER_ACTION_LABELS[player.animation?.action] || player.animation?.action || '持枪架点';
  const role = PLAYER_ROLE_LABELS[player.animation?.role] || player.animation?.role || '战术单位';
  const speed = ['walk', 'run'].includes(player.animation?.action)
    ? `${Math.round(Number(player.animation?.speedUnitsPerSecond || 0))} u/s`
    : '静止';
  ui.focusHud.classList.remove('hidden');
  ui.focusName.textContent = String(player.name).startsWith(`${side} ·`) ? player.name : `${side} · ${player.name}`;
  ui.focusAction.textContent = `${action} · ${Math.max(0, player.health)} HP`;
  ui.focusDetail.textContent = `${role} · ${player.weapon || '未读取武器'} · ${speed}`;
}

function renderCombatFeed(events) {
  const visibleEvents = Array.isArray(events) ? events.slice(-3) : [];
  const signature = visibleEvents.map((event) => `${event.id}:${event.timeMs}`).join('|');
  if (signature === lastCombatFeedSignature) return;
  lastCombatFeedSignature = signature;
  ui.combatFeed.replaceChildren(...visibleEvents.map((event) => {
    const item = document.createElement('div');
    item.className = `combat-event is-${event.type}`;
    item.textContent = event.label;
    return item;
  }));
}

function worldToImage(position) {
  const matrix = activeLayer?.transform?.worldToImage || calibration?.transform?.worldToImage;
  if (!matrix) return null;
  return {
    x: matrix[0][0] * position.x + matrix[0][1] * position.y + matrix[0][2],
    y: matrix[1][0] * position.x + matrix[1][1] * position.y + matrix[1][2],
  };
}

function imageToWorld(image, sourceZ = null) {
  const matrix = activeLayer?.transform?.worldToImage || calibration?.transform?.worldToImage;
  if (!matrix) return null;
  const a = Number(matrix[0][0]);
  const b = Number(matrix[0][1]);
  const c = Number(matrix[0][2]);
  const d = Number(matrix[1][0]);
  const e = Number(matrix[1][1]);
  const f = Number(matrix[1][2]);
  const determinant = a * e - b * d;
  if (![a, b, c, d, e, f, determinant].every(Number.isFinite) || Math.abs(determinant) < 1e-9) return null;
  const u = Number(image?.x);
  const v = Number(image?.y);
  if (!Number.isFinite(u) || !Number.isFinite(v)) return null;
  const layerZ = (Number(activeLayer?.zMin || 0) + Number(activeLayer?.zMax || 0)) / 2;
  return {
    x: ((u - c) * e - b * (v - f)) / determinant,
    y: (a * (v - f) - (u - c) * d) / determinant,
    z: Number.isFinite(Number(sourceZ)) ? Number(sourceZ) : layerZ,
  };
}

function annotationToWorld(annotation) {
  if (annotation?.position) return {
    x: Number(annotation.position.x),
    y: Number(annotation.position.y),
    z: Number(annotation.position.z),
  };
  return imageToWorld(annotation?.image, annotation?.sourceZ);
}

function annotationToImage(annotation) {
  if (annotation?.image) return { x: Number(annotation.image.x), y: Number(annotation.image.y) };
  return annotation?.position ? worldToImage(annotation.position) : null;
}

function annotationVisibleOnActiveLayer(annotation) {
  if (!annotation?.layerId || annotation.layerId === 'all') return true;
  if (Array.isArray(annotation.layerId)) return annotation.layerId.includes(activeLayer?.id);
  return annotation.layerId === activeLayer?.id;
}

function clearTacticalAnnotations3d() {
  annotationRoot.traverse((object) => {
    if (!object.isSprite) return;
    object.material?.map?.dispose?.();
    object.material?.dispose?.();
  });
  annotationRoot.clear();
}

function createAnnotationSprite(annotation, kind) {
  const label = String(annotation.label || '').trim();
  if (!label) return null;
  const color = kind === 'spawn'
    ? (annotation.team === 'CT' ? '#36a9ff' : '#ff9b45')
    : '#6edcff';
  const prefix = kind === 'spawn' ? (annotation.team === 'CT' ? 'CT' : 'T') : '●';
  const canvas = document.createElement('canvas');
  canvas.width = 1024;
  canvas.height = 256;
  const context = canvas.getContext('2d');
  context.clearRect(0, 0, canvas.width, canvas.height);
  context.fillStyle = 'rgba(3, 10, 17, .84)';
  context.beginPath();
  context.roundRect(12, 20, 1000, 216, 54);
  context.fill();
  context.lineWidth = 8;
  context.strokeStyle = color;
  context.stroke();
  context.textBaseline = 'middle';
  context.font = '800 74px "Microsoft YaHei UI", "Segoe UI", sans-serif';
  context.fillStyle = color;
  context.fillText(prefix, 58, 130);
  context.font = '700 68px "Microsoft YaHei UI", "Segoe UI", sans-serif';
  context.fillStyle = '#f5fbff';
  context.fillText(label, 205, 130);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = true;
  texture.anisotropy = Math.min(16, renderer.capabilities.getMaxAnisotropy());
  const material = new THREE.SpriteMaterial({
    map: texture,
    transparent: true,
    depthTest: false,
    depthWrite: false,
    toneMapped: false,
  });
  const sprite = new THREE.Sprite(material);
  const width = THREE.MathUtils.clamp(4.9 + label.length * 0.42, 5.8, 10.5);
  sprite.scale.set(width, width * 0.25, 1);
  sprite.renderOrder = 30;
  sprite.userData.annotationKind = kind;
  sprite.userData.annotationId = annotation.id;
  return sprite;
}

function renderTacticalAnnotations3d() {
  clearTacticalAnnotations3d();
  if (!tacticalAnnotations || !activeLayer) return;
  const groups = [
    ['callout', ui.showCallouts?.checked ? tacticalAnnotations.callouts || [] : []],
    ['spawn', ui.showSpawns?.checked ? tacticalAnnotations.spawns || [] : []],
  ];
  groups.forEach(([kind, annotations]) => annotations.forEach((annotation) => {
    if (!annotationVisibleOnActiveLayer(annotation)) return;
    const sourcePosition = annotationToWorld(annotation);
    if (!sourcePosition || !Object.values(sourcePosition).every(Number.isFinite)) return;
    const sprite = createAnnotationSprite(annotation, kind);
    if (!sprite) return;
    const raised = { ...sourcePosition, z: sourcePosition.z + (kind === 'spawn' ? 74 : 46) };
    sprite.position.copy(sourceToThree(raised));
    annotationRoot.add(sprite);
  }));
}

function activeRadarViewport(targetWidth, targetHeight) {
  const sourceWidth = Number(calibration?.image?.width || 0);
  const sourceHeight = Number(calibration?.image?.height || 0);
  const full = { x: 0, y: 0, width: sourceWidth, height: sourceHeight, focused: false };
  const bounds = activeLayer?.imageBounds;
  if (availableLayers().length < 2 || !bounds || sourceWidth <= 0 || sourceHeight <= 0) return full;

  let width = Number(bounds.width || 0);
  let height = Number(bounds.height || 0);
  const centerX = (Number(bounds.xMin) + Number(bounds.xMax)) / 2;
  const centerY = (Number(bounds.yMin) + Number(bounds.yMax)) / 2;
  if (![width, height, centerX, centerY].every(Number.isFinite) || width <= 0 || height <= 0) return full;

  const targetAspect = Math.max(0.1, targetWidth / Math.max(1, targetHeight));
  if (width / height < targetAspect) width = height * targetAspect;
  else height = width / targetAspect;
  width = Math.min(sourceWidth, Math.max(1, width));
  height = Math.min(sourceHeight, Math.max(1, height));
  const x = THREE.MathUtils.clamp(centerX - width / 2, 0, sourceWidth - width);
  const y = THREE.MathUtils.clamp(centerY - height / 2, 0, sourceHeight - height);
  return { x, y, width, height, focused: true };
}

function drawCombatEvents2d(frame, ctx, toCanvas, ratio) {
  const elapsedMs = Number(frame.demo?.elapsedMs || 0);
  const playerById = new Map(frame.players.map((player) => [String(player.id), player]));
  (frame.demo?.recentCombatEvents || []).filter((event) => (
    ['damage', 'kill'].includes(event.type)
    && elapsedMs >= Number(event.timeMs)
    && elapsedMs - Number(event.timeMs) <= 700
  )).forEach((event) => {
    const attacker = playerById.get(String(event.actorId));
    const victim = playerById.get(String(event.victimId));
    if (!attacker || !victim) return;
    const from = toCanvas(attacker.position);
    const to = toCanvas(victim.position);
    if (!from || !to) return;
    const age = elapsedMs - Number(event.timeMs);
    const alpha = THREE.MathUtils.clamp(1 - age / 700, 0.18, 1);
    const color = event.type === 'kill' ? '#ff405c' : '#ffb05b';
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.strokeStyle = color;
    ctx.lineWidth = (event.type === 'kill' ? 3 : 2) * ratio;
    ctx.setLineDash(event.type === 'kill' ? [] : [6 * ratio, 4 * ratio]);
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.beginPath();
    ctx.arc(to.x, to.y, (event.type === 'kill' ? 12 : 8) * ratio, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  });
}

function draw2d() {
  if (currentView !== '2d' || !radarImage || !calibration || !currentFrame) return;
  const canvas = ui.twoView;
  const rect = canvas.getBoundingClientRect();
  const ratio = Math.min(window.devicePixelRatio || 1, 2);
  const width = Math.max(1, Math.floor(rect.width * ratio));
  const height = Math.max(1, Math.floor(rect.height * ratio));
  if (canvas.width !== width || canvas.height !== height) {
    canvas.width = width;
    canvas.height = height;
  }
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, width, height);
  const viewport = activeRadarViewport(width, height);
  ctx.drawImage(
    radarImage,
    viewport.x, viewport.y, viewport.width, viewport.height,
    0, 0, width, height,
  );
  if (ui.showBuyzones?.checked && buyzoneImage) {
    ctx.drawImage(
      buyzoneImage,
      viewport.x, viewport.y, viewport.width, viewport.height,
      0, 0, width, height,
    );
  }
  const sx = width / viewport.width;
  const sy = height / viewport.height;

  const toCanvas = (position) => {
    const point = worldToImage(position);
    if (!point) return null;
    return { x: (point.x - viewport.x) * sx, y: (point.y - viewport.y) * sy };
  };
  const imageToCanvas = (point) => point
    ? { x: (Number(point.x) - viewport.x) * sx, y: (Number(point.y) - viewport.y) * sy }
    : null;
  const insideCanvas = (point, margin = 30 * ratio) => point
    && point.x >= -margin && point.x <= width + margin
    && point.y >= -margin && point.y <= height + margin;
  const roundedLabel = (x, y, text, color, alpha = 1) => {
    if (!ui.showPlayerLabels?.checked || !text) return;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.font = `600 ${Math.round(11 * ratio)}px "Microsoft YaHei UI", "Segoe UI", sans-serif`;
    ctx.textBaseline = 'middle';
    const paddingX = 6 * ratio;
    const labelHeight = 18 * ratio;
    const labelWidth = ctx.measureText(text).width + paddingX * 2;
    const left = THREE.MathUtils.clamp(x - labelWidth / 2, 4 * ratio, width - labelWidth - 4 * ratio);
    const top = y - 27 * ratio;
    ctx.fillStyle = 'rgba(4, 10, 16, .78)';
    ctx.beginPath();
    ctx.roundRect(left, top - labelHeight / 2, labelWidth, labelHeight, 5 * ratio);
    ctx.fill();
    ctx.fillStyle = color;
    ctx.fillText(text, left + paddingX, top);
    ctx.restore();
  };
  const tacticalLabel = (x, y, text, color, compact = false) => {
    ctx.save();
    const fontSize = Math.round((compact ? 10 : 11.5) * ratio);
    ctx.font = `700 ${fontSize}px "Microsoft YaHei UI", "Segoe UI", sans-serif`;
    ctx.textBaseline = 'middle';
    const paddingX = 6 * ratio;
    const labelHeight = (compact ? 17 : 20) * ratio;
    const labelWidth = ctx.measureText(text).width + paddingX * 2;
    const left = THREE.MathUtils.clamp(x - labelWidth / 2, 3 * ratio, width - labelWidth - 3 * ratio);
    const top = y - (compact ? 17 : 22) * ratio;
    ctx.fillStyle = compact ? 'rgba(3, 10, 17, .68)' : 'rgba(3, 10, 17, .86)';
    ctx.beginPath();
    ctx.roundRect(left, top - labelHeight / 2, labelWidth, labelHeight, 5 * ratio);
    ctx.fill();
    ctx.strokeStyle = color;
    ctx.lineWidth = compact ? 1 : 1.5 * ratio;
    ctx.stroke();
    ctx.fillStyle = '#f5fbff';
    ctx.fillText(text, left + paddingX, top);
    ctx.restore();
  };
  const drawTacticalAnnotation = (annotation, kind) => {
    if (!annotationVisibleOnActiveLayer(annotation)) return;
    const imagePoint = annotationToImage(annotation);
    const point = imageToCanvas(imagePoint);
    if (!insideCanvas(point)) return;
    const color = kind === 'spawn'
      ? (annotation.team === 'CT' ? '#36a9ff' : '#ff9b45')
      : '#6edcff';
    const radius = (kind === 'spawn' ? 8 : 3.5) * ratio;
    ctx.save();
    ctx.fillStyle = kind === 'spawn' ? color : 'rgba(3, 10, 17, .90)';
    ctx.strokeStyle = color;
    ctx.lineWidth = (kind === 'spawn' ? 2.2 : 1.7) * ratio;
    ctx.beginPath();
    ctx.arc(point.x, point.y, radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    if (kind === 'spawn') {
      ctx.fillStyle = '#071018';
      ctx.font = `900 ${Math.round(8 * ratio)}px "Segoe UI", sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(annotation.team === 'CT' ? 'CT' : 'T', point.x, point.y + 0.5 * ratio);
    }
    ctx.restore();
    tacticalLabel(point.x, point.y, annotation.label, color, kind !== 'spawn');
  };
  const drawResourceMarker = (position, color, label, angle = null) => {
    const point = toCanvas(position);
    if (!insideCanvas(point)) return;
    const alpha = activeLayerOpacity(position.z);
    ctx.save();
    ctx.globalAlpha = Math.max(0.28, alpha * 0.8);
    ctx.translate(point.x, point.y);
    if (Number.isFinite(Number(angle))) ctx.rotate(THREE.MathUtils.degToRad(Number(angle) + 90));
    ctx.fillStyle = color;
    ctx.strokeStyle = '#071018';
    ctx.lineWidth = 1.5 * ratio;
    ctx.beginPath();
    ctx.moveTo(0, -5 * ratio);
    ctx.lineTo(4 * ratio, 4 * ratio);
    ctx.lineTo(-4 * ratio, 4 * ratio);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();

    if (label) tacticalLabel(point.x, point.y, label, color, true);
  };
  const drawPlayer = (player) => {
    const point = toCanvas(player.position);
    if (!insideCanvas(point)) return;
    const color = player.local ? '#35e08d' : player.team === 3 ? '#36a9ff' : '#ff9b45';
    const alpha = activeLayerOpacity(player.position.z);
    const radius = 7 * ratio;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.translate(point.x, point.y);
    ctx.rotate(THREE.MathUtils.degToRad(player.angle));
    ctx.fillStyle = color;
    ctx.strokeStyle = '#071018';
    ctx.lineWidth = 2 * ratio;
    ctx.beginPath();
    ctx.moveTo(0, -radius * 1.8);
    ctx.lineTo(radius, radius);
    ctx.lineTo(-radius, radius);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(0, -radius * 1.8);
    ctx.lineTo(0, -radius * 3.3);
    ctx.strokeStyle = color;
    ctx.lineWidth = 2 * ratio;
    ctx.stroke();
    ctx.restore();

    if (player.dead) {
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.strokeStyle = '#ff4e64';
      ctx.lineWidth = 3 * ratio;
      ctx.beginPath();
      ctx.moveTo(point.x - 8 * ratio, point.y - 8 * ratio);
      ctx.lineTo(point.x + 8 * ratio, point.y + 8 * ratio);
      ctx.moveTo(point.x + 8 * ratio, point.y - 8 * ratio);
      ctx.lineTo(point.x - 8 * ratio, point.y + 8 * ratio);
      ctx.stroke();
      ctx.restore();
    }

    const health = THREE.MathUtils.clamp(Number(player.health), 0, 100);
    const barWidth = 28 * ratio;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.fillStyle = 'rgba(4, 10, 16, .82)';
    ctx.fillRect(point.x - barWidth / 2, point.y + 12 * ratio, barWidth, 3 * ratio);
    ctx.fillStyle = health > 65 ? '#35e08d' : health > 30 ? '#f4b94f' : '#ff4e64';
    ctx.fillRect(point.x - barWidth / 2, point.y + 12 * ratio, barWidth * health / 100, 3 * ratio);
    ctx.restore();
    roundedLabel(point.x, point.y, `${player.name} · ${player.dead ? '阵亡' : `${health} HP`}`, color, alpha);
    if (alpha < 1 && activeLayer) {
      const z = Number(player.position.z);
      const above = z > Number(activeLayer.zMax);
      const boundary = above ? Number(activeLayer.zMax) : Number(activeLayer.zMin);
      tacticalLabel(point.x, point.y + 30 * ratio, `${above ? '↑' : '↓'} ${Math.round(Math.abs(z - boundary))} Z`, color, true);
    }
  };
  const drawPoint = (position, color, label, shape = 'circle', alpha = 1) => {
    const point = toCanvas(position);
    if (!insideCanvas(point)) return;
    const radius = 5 * ratio;
    ctx.save();
    ctx.globalAlpha = alpha * activeLayerOpacity(position.z);
    ctx.translate(point.x, point.y);
    ctx.fillStyle = color;
    ctx.strokeStyle = '#071018';
    ctx.lineWidth = 2 * ratio;
    ctx.beginPath();
    if (shape === 'diamond') {
      ctx.moveTo(0, -radius);
      ctx.lineTo(radius, 0);
      ctx.lineTo(0, radius);
      ctx.lineTo(-radius, 0);
      ctx.closePath();
    } else {
      ctx.arc(0, 0, radius, 0, Math.PI * 2);
    }
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    roundedLabel(point.x, point.y + 2 * ratio, label, color, alpha);
  };
  const drawProjectile = (item) => {
    const color = item.type === 'smoke'
      ? '#aebbc3'
      : item.type === 'inferno'
        ? '#ff6a35'
        : item.type === 'flashbang'
          ? '#effcff'
          : '#ffb13b';
    const label = item.type === 'smoke'
      ? '烟雾'
      : item.type === 'inferno'
        ? '燃烧区域'
        : item.type === 'flashbang'
          ? '闪光'
          : item.type === 'hegrenade'
            ? '爆炸手雷'
            : '投掷物';
    if (item.state === 'detonated' || item.exploded) {
      const point = toCanvas(item.position);
      const edge = toCanvas({ ...item.position, x: Number(item.position.x) + Math.max(48, Number(item.effectRadius || 160)) });
      if (insideCanvas(point) && edge) {
        const effectProgress = THREE.MathUtils.clamp(Number(item.effectAgeMs || 0) / Math.max(1, Number(item.effectDurationMs || 1000)), 0, 1);
        const effectRadius = THREE.MathUtils.clamp(Math.hypot(edge.x - point.x, edge.y - point.y), 12 * ratio, 140 * ratio);
        const alpha = item.type === 'smoke'
          ? 0.18 + Math.min(1, Number(item.effectAgeMs || 0) / 1800) * 0.20
          : (1 - effectProgress) * 0.42;
        ctx.save();
        ctx.globalAlpha = alpha * activeLayerOpacity(item.position.z);
        ctx.fillStyle = color;
        ctx.strokeStyle = color;
        ctx.lineWidth = 2 * ratio;
        ctx.beginPath();
        ctx.arc(point.x, point.y, effectRadius, 0, Math.PI * 2);
        if (item.type === 'smoke' || item.type === 'inferno') ctx.fill();
        ctx.stroke();
        ctx.restore();
      }
    }
    drawPoint(item.position, color, label, item.state === 'detonated' ? 'diamond' : 'circle');
  };

  if (ui.showCallouts?.checked) {
    (tacticalAnnotations?.callouts || []).forEach((annotation) => drawTacticalAnnotation(annotation, 'callout'));
  }
  if (ui.showSpawns?.checked) {
    (tacticalAnnotations?.spawns || []).forEach((annotation) => drawTacticalAnnotation(annotation, 'spawn'));
  }
  if (ui.showLineups?.checked) {
    (mapGrenadeKnowledge?.entries || []).slice(0, 12).forEach((entry) => {
      const position = { x: Number(entry.position?.[0]), y: Number(entry.position?.[1]), z: Number(entry.position?.[2]) };
      drawResourceMarker(position, '#c788ff', '', entry.angle?.[1]);
    });
  }
  if (ui.showMapEntities?.checked) {
    const visibleClasses = new Set(['func_breakable', 'func_nav_blocker', 'func_door_rotating', 'prop_door_rotating']);
    (mapDynamicEntities?.entities || [])
      .filter((entry) => visibleClasses.has(entry.className) && entry.origin?.some((value) => Number(value) !== 0))
      .slice(0, 32)
      .forEach((entry) => drawResourceMarker(
        { x: Number(entry.origin[0]), y: Number(entry.origin[1]), z: Number(entry.origin[2]) },
        '#ffd166',
        entry.className.includes('door') ? '门' : entry.className === 'func_nav_blocker' ? '阻断区' : '可破坏',
      ));
  }

  if (ui.showTrails?.checked) {
    for (const [id, trail] of playerTrails) {
      if (trail.length < 2) continue;
      const player = currentFrame.players.find((candidate) => String(candidate.id) === id);
      const color = player?.local ? '#35e08d' : player?.team === 3 ? '#36a9ff' : '#ff9b45';
      ctx.save();
      ctx.strokeStyle = color;
      ctx.lineWidth = 1.5 * ratio;
      ctx.globalAlpha = 0.32 * activeLayerOpacity(trail.at(-1)?.z);
      ctx.beginPath();
      trail.forEach((sample, index) => {
        const point = toCanvas(sample);
        if (!point) return;
        if (index === 0) ctx.moveTo(point.x, point.y);
        else ctx.lineTo(point.x, point.y);
      });
      ctx.stroke();
      ctx.restore();
    }
  }

  drawCombatEvents2d(currentFrame, ctx, toCanvas, ratio);

  currentFrame.players.filter((p) => (demoMode && p.animation?.action === 'death') || ui.showDead.checked || !p.dead).forEach(drawPlayer);
  if (currentFrame.bomb && !currentFrame.bomb.defused) {
    drawPoint(currentFrame.bomb.position, '#ff3557', 'C4', 'diamond');
  }
  currentFrame.projectiles.forEach(drawProjectile);
  currentFrame.droppedWeapons.forEach((item) => {
    drawPoint(item.position, '#b982ff', item.name || item.weapon || '掉落武器', 'diamond', 0.88);
  });
}

function applyFrame(frame) {
  currentFrame = frame;
  lastFrameAt = performance.now();
  if (!demoMode) {
    liveProviderConnected = true;
    lastLiveHealthState = '';
  }
  updatePlayerTrails(frame);
  const reference = frame.players.find((player) => player.local && !player.dead)
    || frame.players.find((player) => !player.dead);
  if (reference) {
    lastReferenceHeight = Number(reference.position.z);
    if (ui.layerMode?.value !== 'manual') {
      const match = selectLayerForHeight(
        availableLayers(),
        lastReferenceHeight,
        activeLayer,
        floorIndex?.selection?.hysteresisUnits ?? 24,
      );
      if (match) setActiveLayer(match.layer, match.reason, lastReferenceHeight);
    }
  }
  renderEntities(frame);
  draw2d();
  if (frame.demo) updateDemoControls(frame.demo);
  else ui.roundHud?.classList.add('hidden');
}

async function acceptFrame(frame) {
  const mapId = normalizeMapId(frame.mapId);
  pendingFrame = frame;
  try {
    await loadMapPackage(mapId);
    if (normalizeMapId(pendingFrame?.mapId) === mapId) {
      applyFrame(demoMode ? createPreviewFrame(mapId) : pendingFrame);
    }
  } catch (error) {
    const entry = findMapEntry(mapRegistry, mapId);
    setStatus('error', entry ? `${entry.displayName}尚未就绪` : '地图未收录', 'MAP');
    ui.frameState.textContent = error.message;
  }
}

function formatDemoTime(milliseconds) {
  const seconds = Math.max(0, Math.floor(Number(milliseconds) / 1000));
  return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
}

function alivePips(value) {
  const alive = THREE.MathUtils.clamp(Math.round(Number(value) || 0), 0, 5);
  return `${'● '.repeat(alive)}${'○ '.repeat(5 - alive)}`.trim();
}

function updateRoundHud(demo) {
  const aliveCt = Number(demo.alive?.ct ?? 5);
  const aliveT = Number(demo.alive?.t ?? 5);
  const objectiveState = String(demo.objectiveState || 'inactive');
  let objectiveLabel = DEMO_OBJECTIVE_LABELS[objectiveState] || '回合进行中';
  if (objectiveState === 'planted' && Number.isFinite(Number(demo.bombClockMs))) {
    objectiveLabel = `C4 ${formatDemoTime(demo.bombClockMs)}`;
  } else if (objectiveState === 'defusing' && Number.isFinite(Number(demo.defuseClockMs))) {
    objectiveLabel = `拆包 ${formatDemoTime(demo.defuseClockMs)}`;
  }
  ui.roundHud.classList.remove('hidden');
  ui.roundHud.classList.toggle('is-finished', Boolean(demo.roundFinished));
  ui.roundCtAlive.textContent = String(aliveCt);
  ui.roundTAlive.textContent = String(aliveT);
  ui.roundCtPips.textContent = alivePips(aliveCt);
  ui.roundTPips.textContent = alivePips(aliveT);
  ui.roundObjective.textContent = demo.roundFinished ? demo.resultLabel || objectiveLabel : objectiveLabel;
  ui.roundResult.textContent = demo.roundFinished
    ? `${Number(demo.winnerTeam) === 3 ? 'CT' : 'T'} 赢得回合`
    : `CT ${aliveCt} · ${aliveT} T`;
}

function renderDemoEventRail(demo) {
  const events = Array.isArray(demo?.timelineEvents) ? demo.timelineEvents : [];
  const duration = Math.max(1000, Number(demo?.durationMs) || 30000);
  const signature = `${demo?.scenarioId || ''}:${events.map((event) => `${event.id}@${event.timeMs}`).join('|')}`;
  if (signature !== lastDemoEventRailSignature) {
    lastDemoEventRailSignature = signature;
    demoEventButtonById.clear();
    const laneLastTimeMs = [];
    const railWidth = Math.max(160, Number(ui.demoEventRail.clientWidth) || 230);
    const minimumTimeGapMs = duration * 20 / railWidth;
    let maximumLane = 0;
    ui.demoEventRail.replaceChildren(...events.map((event) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `demo-event-marker type-${event.type}`;
      button.style.left = `${THREE.MathUtils.clamp(Number(event.timeMs) / duration * 100, 0, 100)}%`;
      let lane = laneLastTimeMs.findIndex((lastTimeMs) => Number(event.timeMs) - lastTimeMs >= minimumTimeGapMs);
      if (lane < 0) lane = laneLastTimeMs.length;
      laneLastTimeMs[lane] = Number(event.timeMs);
      maximumLane = Math.max(maximumLane, lane);
      button.style.setProperty('--event-lane-top', `${-6 + lane * 12}px`);
      button.title = `${formatDemoTime(event.timeMs)} · ${event.label}`;
      button.setAttribute('aria-label', button.title);
      button.addEventListener('click', () => {
        if (!demoMode) return;
        demoPaused = true;
        demoElapsedMs = Number(event.timeMs) || 0;
        void acceptFrame(createPreviewFrame(demoMapId));
      });
      demoEventButtonById.set(event.id, button);
      return button;
    }));
    ui.demoEventRail.style.height = `${Math.max(36, maximumLane * 12 + 20)}px`;
  }
  const activeId = demo?.activeEvent?.id;
  demoEventButtonById.forEach((button, id) => {
    const event = events.find((candidate) => candidate.id === id);
    button.classList.toggle('is-active', id === activeId);
    button.classList.toggle('is-past', Number(event?.timeMs) < Number(demo?.elapsedMs));
  });
  ui.demoEventDetail.textContent = demo?.activeEvent
    ? `${formatDemoTime(demo.activeEvent.timeMs)} · ${demo.activeEvent.label}`
    : '等待战术事件';
}

function populateDemoScenarioControls() {
  const scenarios = mapDemoScenarios?.scenarios || [];
  const previous = Number(ui.demoScenario?.value || 0);
  ui.demoScenario.replaceChildren(...scenarios.map((scenario, index) => {
    const option = document.createElement('option');
    option.value = String(index);
    option.textContent = `${index + 1}. ${scenario.name}`;
    return option;
  }));
  ui.demoScenario.disabled = !demoMode || scenarios.length === 0;
  ui.demoPlayPause.disabled = !demoMode || scenarios.length === 0;
  ui.demoTimeline.disabled = !demoMode || scenarios.length === 0;
  if (scenarios.length) ui.demoScenario.value = String(Math.min(previous, scenarios.length - 1));
  ui.demoState.textContent = scenarios.length
    ? `${scenarios.length} 个确定性场景 · 语义图路径`
    : '实机锚点回放';
  if (!scenarios.length) {
    lastDemoEventRailSignature = '';
    demoEventButtonById.clear();
    ui.demoEventRail.replaceChildren();
    ui.demoEventDetail.textContent = '等待战术事件';
  }
}

function updateDemoControls(demo) {
  const duration = Math.max(1000, Number(demo.durationMs) || 30000);
  ui.demoTimeline.value = String(Math.round(Number(demo.progress) * 1000));
  const liveClock = demo.bombPlanted
    ? `C4 ${formatDemoTime(Number(demo.bombClockMs) || 0)}`
    : `回合 ${formatDemoTime(Number(demo.roundClockMs) || 0)}`;
  ui.demoTime.textContent = `${liveClock} · ${formatDemoTime(Number(demo.progress) * duration)} / ${formatDemoTime(duration)}`;
  ui.demoPlayPause.textContent = demoPaused ? '继续' : '暂停';
  ui.demoSpeed.value = String(demoPlaybackRate);
  ui.demoDirector.setAttribute('aria-pressed', String(demoDirectorEnabled));
  ui.demoDirector.textContent = demoDirectorEnabled ? '导演：开启' : '导演：关闭';
  const usesOfficialNavigation = String(demo.navigationMode || '').startsWith('official-navmesh-a-star');
  ui.demoState.textContent = `${demo.scenarioName} · ${demo.tacticalPhase || '战术展开'} · ${usesOfficialNavigation ? '可行走路线' : '锚点路径'}`;
  updateRoundHud(demo);
  renderDemoEventRail(demo);
}

function createPreviewFrame(mapId, time = demoElapsedMs) {
  return mapDemoScenarios
    ? createScenarioDemoFrame(normalizeMapId(mapId), mapDemoScenarios, mapLiveValidation, time, Number(ui.demoScenario?.value || 0))
    : createResourceDemoFrame(normalizeMapId(mapId), mapLiveValidation, time);
}

function disconnectLive() {
  if (liveReconnectTimer) {
    clearTimeout(liveReconnectTimer);
    liveReconnectTimer = null;
  }
  liveWsUrl = '';
  liveReconnectAttempt = 0;
  liveProviderConnected = false;
  lastLiveHealthState = '';
  lastFrameAt = 0;
  if (liveSocket) {
    liveSocket.onclose = null;
    liveSocket.close();
    liveSocket = null;
  }
}

function hideConnectionLanding() {
  ui.entryLanding?.classList.add('hidden');
}

function showConnectionLanding() {
  disconnectLive();
  demoMode = false;
  demoPaused = true;
  populateDemoScenarioControls();
  ui.mapSelect.disabled = false;
  ui.entryLanding?.classList.remove('hidden');
  if (ui.entryTitle) ui.entryTitle.textContent = '等待共享雷达连接';
  if (ui.entryDetail) {
    ui.entryDetail.textContent = '请在 POW 软件的雷达设置中点击“连接并打开共享雷达”。软件会自动创建房间并进入，无需管理员授权码。';
  }
  setStatus('wait', '等待共享雷达连接', 'READY');
  ui.frameState.textContent = '等待 POW 软件创建共享房间';
}

async function startDemo() {
  disconnectLive();
  hideConnectionLanding();
  demoMode = true;
  setThreeDimensionalAccess(true);
  ui.mapSelect.disabled = false;
  demoMapId = normalizeMapId(ui.mapSelect.value || mapRegistry.defaultMapId);
  demoElapsedMs = 0;
  demoPaused = false;
  lastDirectedEventId = '';
  directorTransitionActive = false;
  setStatus('ok', '离线演示', 'LOCAL');
  await acceptFrame(createPreviewFrame(demoMapId));
  populateDemoScenarioControls();
}

function openLiveSocket(url) {
  liveWsUrl = url;
  try {
    liveSocket = new WebSocket(url);
  } catch (error) {
    setStatus('error', '地址无效', 'ERROR');
    ui.frameState.textContent = error.message;
    return;
  }
  setStatus('wait', '正在连接', 'WS');
  liveSocket.onopen = () => {
    liveReconnectAttempt = 0;
    liveProviderConnected = false;
    lastLiveHealthState = '';
    lastFrameAt = 0;
    setStatus('wait', '已进入房间，等待数据源', 'ROOM');
  };
  liveSocket.onmessage = (event) => {
    try {
      const payload = JSON.parse(event.data);
      if (payload?.type === 'csgo2-radar/control/1') {
        liveProviderConnected = payload.providerConnected === true;
        lastLiveHealthState = '';
        if (liveProviderConnected) {
          setStatus('wait', '数据源已连接，等待最新帧', 'SYNC');
        } else {
          setStatus('wait', '数据源已断开，保留最后画面', 'HOLD');
        }
        return;
      }
      liveProviderConnected = true;
      lastLiveHealthState = '';
      void acceptFrame(adaptCs2Frame(payload));
    } catch (error) {
      ui.frameState.textContent = error.message;
    }
  };
  liveSocket.onerror = () => setStatus('error', '连接异常', 'ERROR');
  liveSocket.onclose = () => {
    liveSocket = null;
    liveProviderConnected = false;
    lastLiveHealthState = '';
    if (!demoMode && liveWsUrl === url) {
      const delay = Math.min(8000, 500 * (2 ** liveReconnectAttempt));
      liveReconnectAttempt = Math.min(liveReconnectAttempt + 1, 5);
      setStatus('wait', `连接中断，${Math.ceil(delay / 1000)} 秒后重试`, 'RETRY');
      liveReconnectTimer = setTimeout(() => {
        liveReconnectTimer = null;
        if (!demoMode && liveWsUrl === url) openLiveSocket(url);
      }, delay);
    }
  };
}

function syncConnectionModeUi() {
  const official = ui.connectionMode.value === 'official';
  ui.officialFields.classList.toggle('hidden', !official);
  ui.directFields.classList.toggle('hidden', official);
  ui.connect.textContent = official ? '加入房间' : '连接 WebSocket';
}

async function connectOwnerEntry(roomId, ticket) {
  disconnectLive();
  hideConnectionLanding();
  demoMode = false;
  demoPaused = true;
  populateDemoScenarioControls();
  ui.mapSelect.disabled = true;
  ui.connectionMode.value = 'official';
  syncConnectionModeUi();
  ui.roomId.value = roomId;

  const cleanUrl = new URL(location.href);
  cleanUrl.searchParams.delete('ticket');
  history.replaceState(null, '', `${cleanUrl.pathname}${cleanUrl.search}${cleanUrl.hash}`);
  try {
    setStatus('wait', '正在通过软件安全进入房间', 'AUTH');
    const response = await fetch(`/api/rooms/${encodeURIComponent(roomId)}/owner-entry`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ticket }),
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) {
      const message = result.code === 'room_not_found'
        ? '房间不存在或已过期'
        : result.code === 'owner_entry_invalid'
          ? '自动进入链接已使用或已过期，请输入房间密码'
          : result.code === 'rate_limited'
            ? '尝试次数过多，请稍后再试'
            : '自动进入失败，请输入房间密码';
      throw new Error(message);
    }
    const wsPath = String(result.viewer?.wsPath || '');
    if (!wsPath.startsWith('/ws?')) throw new Error('服务器未返回有效连接地址');
    setThreeDimensionalAccess(result.allow3d === true);
    const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
    openLiveSocket(`${protocol}//${location.host}${wsPath}`);
  } catch (error) {
    demoMode = false;
    ui.mapSelect.disabled = false;
    setStatus('error', error.message || '自动进入失败', 'AUTH');
    ui.frameState.textContent = error.message || '自动进入失败';
    ui.roomPassword.focus();
  }
}

async function connectLive() {
  disconnectLive();
  hideConnectionLanding();
  demoMode = false;
  populateDemoScenarioControls();
  ui.mapSelect.disabled = true;
  const official = ui.connectionMode.value === 'official';
  const roomId = ui.roomId.value.trim().toUpperCase();
  const roomPassword = ui.roomPassword.value;
  let url = ui.wsUrl.value.trim();
  if (official) {
    if (!/^[A-Z0-9_-]{4,32}$/.test(roomId) || roomPassword.length < 4 || roomPassword.length > 64) {
      demoMode = false;
      populateDemoScenarioControls();
      setStatus('error', '请填写房间码与 4–64 位房间密码', 'AUTH');
      ui.mapSelect.disabled = false;
      return;
    }
    try {
      ui.connect.disabled = true;
      setStatus('wait', '正在验证房间', 'AUTH');
      const response = await fetch(`/api/rooms/${encodeURIComponent(roomId)}/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: roomPassword }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        const message = result.code === 'room_not_found'
          ? '房间不存在或已过期'
          : result.code === 'invalid_room_password'
            ? '房间密码错误'
            : result.code === 'rate_limited'
              ? '尝试次数过多，请稍后再试'
              : '房间验证失败';
        throw new Error(message);
      }
      const wsPath = String(result.viewer?.wsPath || '');
      if (!wsPath.startsWith('/ws?')) throw new Error('服务端未返回有效连接地址');
      setThreeDimensionalAccess(result.allow3d === true);
      const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
      url = `${protocol}//${location.host}${wsPath}`;
      ui.roomId.value = roomId;
      ui.roomPassword.value = '';
    } catch (error) {
      demoMode = false;
      populateDemoScenarioControls();
      ui.mapSelect.disabled = false;
      setStatus('error', error.message || '房间验证失败', 'AUTH');
      ui.frameState.textContent = error.message;
      ui.connect.disabled = false;
      return;
    }
    ui.connect.disabled = false;
  } else {
    setThreeDimensionalAccess(true);
  }
  openLiveSocket(url);
}

function setThreeDimensionalAccess(allowed) {
  roomAllows3d = Boolean(allowed);
  const button = document.querySelector('.mode[data-view="3d"]');
  if (button) {
    button.disabled = !roomAllows3d;
    button.title = roomAllows3d ? '' : '房主已将该房间设为仅 2D 雷达';
  }
  if (!roomAllows3d && currentView === '3d') setView('2d');
}

function setView(view) {
  currentView = view === '3d' && !roomAllows3d ? '2d' : view;
  ui.threeView.classList.toggle('hidden', currentView !== '3d');
  ui.twoView.classList.toggle('hidden', currentView !== '2d');
  document.querySelectorAll('.mode').forEach((button) => button.classList.toggle('active', button.dataset.view === currentView));
  ui.viewHint.textContent = currentView === '3d'
    ? '左键旋转 · 右键平移 · 滚轮缩放'
    : '2D 实测校准 · 玩家、朝向、血量、轨迹与楼层同步';
  resize();
  draw2d();
  queueGeometryLoad();
}

function resize() {
  const rect = ui.threeView.getBoundingClientRect();
  renderer.setSize(Math.max(1, rect.width), Math.max(1, rect.height), true);
  camera.aspect = Math.max(1, rect.width) / Math.max(1, rect.height);
  camera.updateProjectionMatrix();
  composer.setSize(Math.max(1, rect.width), Math.max(1, rect.height));
  fxaaPass.material.uniforms.resolution.value.set(
    1 / Math.max(1, composer.readBuffer.width),
    1 / Math.max(1, composer.readBuffer.height),
  );
  draw2d();
}

function updateClipping() {
  const sourceHeight = Number(ui.heightCut.value);
  clippingPlane.constant = sourceHeight * SOURCE_TO_METRES;
  const layerMinimum = Number(activeLayer?.zMin ?? ui.heightCut.min ?? sourceHeight);
  // Keep a small amount of lower-floor context and avoid clipping coplanar floor faces.
  mapContextFloorPlane.constant = -(layerMinimum - 64.5) * SOURCE_TO_METRES;
  const planes = ui.roofCut.checked ? [clippingPlane, mapContextFloorPlane] : [];
  forEachMapMaterial((material) => {
    material.clippingPlanes = planes;
    const uniforms = material.userData?.radarUniforms;
    if (uniforms?.radarLayerCrop) uniforms.radarLayerCrop.value = shouldCropToLayer(activeLayer) ? 1 : 0;
    if (uniforms?.radarLayerFloor) uniforms.radarLayerFloor.value = layerMinimum;
    if (uniforms?.radarLayerContextStrength) {
      uniforms.radarLayerContextStrength.value = availableLayers().length > 1 ? 0.58 : 0.18;
    }
  });
  if (mapEdges) {
    mapEdges.material.clippingPlanes = planes;
    mapEdges.visible = shouldShowMapEdges(ui.renderStyle?.value, ui.renderQuality?.value)
      && !shouldCropToLayer(activeLayer);
  }
  const normalPlanes = planes.slice();
  if (shouldCropToLayer(activeLayer) && radarProjection) {
    const bounds = normalizedLayerUvBounds(activeLayer);
    const { row0, row1 } = radarProjection;
    const uNormal = new THREE.Vector3(row0.x / SOURCE_TO_METRES, 0, -row0.y / SOURCE_TO_METRES);
    const vNormal = new THREE.Vector3(-row1.x / SOURCE_TO_METRES, 0, row1.y / SOURCE_TO_METRES);
    normalPlanes.push(
      new THREE.Plane(uNormal.clone(), row0.z - bounds.x).normalize(),
      new THREE.Plane(uNormal.clone().negate(), bounds.z - row0.z).normalize(),
      new THREE.Plane(vNormal.clone(), 1 - row1.z - bounds.y).normalize(),
      new THREE.Plane(vNormal.clone().negate(), bounds.w - 1 + row1.z).normalize(),
    );
  }
  ssaoPass.normalMaterial.clippingPlanes = normalPlanes;
  ui.heightValue.textContent = `${sourceHeight} Z`;
}

function clearPlayerFocus() {
  focusedPlayerId = null;
  playerEntityRoot.children.forEach((object) => {
    object.visible = true;
    if (object.userData.directionIndicator) object.userData.directionIndicator.visible = true;
    if (object.userData.healthRing) object.userData.healthRing.visible = true;
  });
  transientEntityRoot.visible = true;
  projectileEntityRoot.visible = true;
  annotationRoot.visible = true;
  updateFocusHud(null);
}

document.querySelectorAll('.mode').forEach((button) => button.addEventListener('click', () => setView(button.dataset.view)));
ui.connect.addEventListener('click', () => void connectLive());
ui.roomPassword?.addEventListener('keydown', (event) => {
  if (event.key === 'Enter') void connectLive();
});
ui.demo.addEventListener('click', startDemo);
ui.entryDemo.addEventListener('click', startDemo);
ui.entryJoin.addEventListener('click', () => {
  hideConnectionLanding();
  demoMode = false;
  setStatus('wait', '请输入房间号和房间密码', 'ROOM');
  ui.roomId.focus();
});
ui.fit.addEventListener('click', () => {
  clearPlayerFocus();
  const players = currentFrame?.players || [];
  if (!players.length) return fitMap();
  const box = new THREE.Box3();
  players.forEach((player) => box.expandByPoint(sourceToThree(player.position)));
  const center = box.getCenter(new THREE.Vector3());
  controls.target.copy(center);
  camera.position.set(center.x + 24, center.y + 34, center.z + 24);
});
ui.focusExit?.addEventListener('click', () => ui.fit.click());
ui.renderStyle?.addEventListener('change', applyRenderStyle);
ui.mapTextureBlend?.addEventListener('input', () => {
  const value = Number(ui.mapTextureBlend.value);
  ui.mapTextureValue.textContent = `${value}%`;
  const style = resolveRenderProfile(currentMapId, ui.renderStyle?.value);
  forEachMapMaterial((material) => {
    const uniforms = material.userData?.radarUniforms;
    const affinity = Number(material.userData?.radarSurfaceAppearance?.mapAffinity ?? 1);
    if (uniforms?.radarMapBlend) {
      uniforms.radarMapBlend.value = radarProjection ? selectedMapTextureStrength(style) * affinity : 0;
    }
  });
});
ui.showBuyzones?.addEventListener('change', () => {
  forEachMapMaterial((material) => {
    const uniforms = material.userData?.radarUniforms;
    const affinity = Number(material.userData?.radarSurfaceAppearance?.mapAffinity ?? 1);
    if (uniforms?.radarBuyzoneBlend) {
      uniforms.radarBuyzoneBlend.value = radarProjection?.buyzoneTexture && ui.showBuyzones.checked
        ? 0.72 * affinity : 0;
    }
  });
  draw2d();
});
ui.showSpawns?.addEventListener('change', () => {
  renderTacticalAnnotations3d();
  draw2d();
});
ui.showCallouts?.addEventListener('change', () => {
  renderTacticalAnnotations3d();
  draw2d();
});
ui.showLineups?.addEventListener('change', draw2d);
ui.showMapEntities?.addEventListener('change', draw2d);
ui.demoPlayPause?.addEventListener('click', () => {
  const durationMs = Number(currentFrame?.demo?.durationMs || 0);
  if (demoPaused && durationMs > 0 && demoElapsedMs >= durationMs - 1) demoElapsedMs = 0;
  demoPaused = !demoPaused;
  ui.demoPlayPause.textContent = demoPaused ? '继续' : '暂停';
});
ui.demoSpeed?.addEventListener('change', () => {
  const requested = Number(ui.demoSpeed.value);
  demoPlaybackRate = [0.5, 1, 2].includes(requested) ? requested : 1;
  ui.demoSpeed.value = String(demoPlaybackRate);
});
ui.demoRestart?.addEventListener('click', () => {
  if (!demoMode) return;
  demoElapsedMs = 0;
  demoPaused = false;
  lastDirectedEventId = '';
  directorTransitionActive = false;
  clearPlayerFocus();
  resetPlayerTrails();
  void acceptFrame(createPreviewFrame(demoMapId));
});
ui.demoDirector?.addEventListener('click', () => {
  demoDirectorEnabled = !demoDirectorEnabled;
  lastDirectedEventId = '';
  directorTransitionActive = false;
  ui.demoDirector.setAttribute('aria-pressed', String(demoDirectorEnabled));
  ui.demoDirector.textContent = demoDirectorEnabled ? '导演：开启' : '导演：关闭';
  if (demoDirectorEnabled) {
    clearPlayerFocus();
    if (currentFrame?.demo) directCameraToDemoEvent(currentFrame);
  }
});
ui.demoScenario?.addEventListener('change', () => {
  demoElapsedMs = 0;
  clearPlayerFocus();
  lastDirectedEventId = '';
  directorTransitionActive = false;
  lastDemoEventRailSignature = '';
  resetPlayerTrails();
  if (demoMode) void acceptFrame(createPreviewFrame(demoMapId));
});
ui.demoTimeline?.addEventListener('input', () => {
  const scenario = mapDemoScenarios?.scenarios?.[Number(ui.demoScenario.value || 0)];
  if (!scenario) return;
  demoPaused = true;
  demoElapsedMs = Number(ui.demoTimeline.value) / 1000 * Number(scenario.durationMs || 30000);
  lastDirectedEventId = '';
  directorTransitionActive = false;
  void acceptFrame(createPreviewFrame(demoMapId));
});
ui.renderQuality?.addEventListener('change', applyRenderQuality);
ui.layerMode?.addEventListener('change', () => {
  const manual = ui.layerMode.value === 'manual';
  ui.layerSelect.disabled = !manual || availableLayers().length < 2;
  if (manual) {
    const layer = availableLayers().find((item) => item.id === ui.layerSelect.value) || activeLayer;
    setActiveLayer(layer, 'manual', lastReferenceHeight);
  } else if (Number.isFinite(lastReferenceHeight)) {
    const match = selectLayerForHeight(
      availableLayers(),
      lastReferenceHeight,
      activeLayer,
      floorIndex?.selection?.hysteresisUnits ?? 24,
    );
    if (match) setActiveLayer(match.layer, match.reason, lastReferenceHeight);
  } else {
    describeLayerSelection('waiting');
  }
});
ui.layerSelect?.addEventListener('change', () => {
  if (ui.layerMode.value !== 'manual') return;
  const layer = availableLayers().find((item) => item.id === ui.layerSelect.value);
  setActiveLayer(layer, 'manual', lastReferenceHeight);
  fitMap();
});
ui.connectionMode?.addEventListener('change', () => {
  syncConnectionModeUi();
});
ui.roofCut.addEventListener('change', updateClipping);
ui.heightCut.addEventListener('input', updateClipping);
ui.showDead.addEventListener('change', () => currentFrame && acceptFrame(currentFrame));
ui.showPlayerLabels?.addEventListener('change', draw2d);
ui.showTrails?.addEventListener('change', () => {
  if (!ui.showTrails.checked) resetPlayerTrails();
  draw2d();
});
ui.mapSelect.addEventListener('change', () => {
  if (!demoMode) return;
  demoMapId = normalizeMapId(ui.mapSelect.value);
  demoElapsedMs = 0;
  clearPlayerFocus();
  lastDirectedEventId = '';
  directorTransitionActive = false;
  lastDemoEventRailSignature = '';
  void acceptFrame(createPreviewFrame(demoMapId));
});
new ResizeObserver(resize).observe(document.querySelector('.stage'));

function animate(time) {
  requestAnimationFrame(animate);
  const delta = lastAnimationTime > 0 ? Math.min(100, Math.max(0, time - lastAnimationTime)) : 0;
  lastAnimationTime = time;
  const demoWasPlaying = demoMode && !demoPaused;
  if (demoWasPlaying) {
    const durationMs = Number(currentFrame?.demo?.durationMs || 0);
    demoElapsedMs += delta * demoPlaybackRate;
    if (durationMs > 0 && demoElapsedMs >= durationMs) {
      demoElapsedMs = durationMs;
      demoPaused = true;
    }
  }
  if (demoWasPlaying && mapRegistry && currentMapId && time - lastDemoUpdate > 100) {
    lastDemoUpdate = time;
    void acceptFrame(createPreviewFrame(demoMapId, demoElapsedMs));
  }
  if (!demoMode && liveSocket?.readyState === WebSocket.OPEN) {
    const ageMs = lastFrameAt > 0
      ? Math.max(0, performance.now() - lastFrameAt)
      : Number.POSITIVE_INFINITY;
    let state = 'waiting-provider';
    let kind = 'wait';
    let message = '已进入房间，等待数据源';
    let badge = 'ROOM';
    if (!liveProviderConnected) {
      state = 'provider-disconnected';
      message = '数据源已断开，保留最后画面';
      badge = 'HOLD';
    } else if (!Number.isFinite(ageMs)) {
      state = 'waiting-frame';
      message = '数据源已连接，等待最新帧';
      badge = 'SYNC';
    } else if (ageMs < 1000) {
      state = 'live';
      kind = 'ok';
      message = roomAllows3d ? 'CSGO2 实时' : 'CSGO2 实时 · 2D 房间';
      badge = `${Math.round(ageMs)}ms`;
    } else if (ageMs < 3000) {
      state = 'delayed';
      message = `数据延迟 ${(ageMs / 1000).toFixed(1)} 秒`;
      badge = 'DELAY';
    } else {
      state = 'stale';
      kind = 'error';
      message = `数据已过期 ${(ageMs / 1000).toFixed(1)} 秒`;
      badge = 'STALE';
    }
    if (state !== lastLiveHealthState) {
      setStatus(kind, message, badge);
      lastLiveHealthState = state;
    } else if (state === 'live') {
      ui.latency.textContent = badge;
    }
  }
  updatePlayerTransforms(time);
  updateOperatorAnimations(time);
  updateProjectileEffects(time);
  updateDirectorCameraTransition();
  controls.update();
  if (currentView === '3d') {
    const uniforms = ssaoPass.ssaoMaterial.uniforms;
    uniforms.cameraNear.value = camera.near;
    uniforms.cameraFar.value = camera.far;
    uniforms.cameraProjectionMatrix.value.copy(camera.projectionMatrix);
    uniforms.cameraInverseProjectionMatrix.value.copy(camera.projectionMatrixInverse);
    composer.render();
  }
}

applyRenderStyle();
applyRenderQuality();
syncConnectionModeUi();
updateClipping();
resize();
setView(currentView);
void loadOperatorModels();
loadMapRegistry()
  .then(() => loadMapPackage(mapRegistry.defaultMapId))
  .then(async () => {
    const query = new URLSearchParams(location.search);
    const requestedView = String(query.get('view') || '').toLowerCase();
    if (requestedView === '2d' || requestedView === '3d') setView(requestedView);
    const requestedRoom = String(query.get('room') || '').trim().toUpperCase();
    const requestedTicket = String(query.get('ticket') || '');
    const requestedWs = query.get('ws');
    if (requestedWs) {
      ui.connectionMode.value = 'direct';
      syncConnectionModeUi();
      ui.wsUrl.value = requestedWs;
      await connectLive();
    } else if (/^[A-Z0-9_-]{4,32}$/.test(requestedRoom) && requestedTicket) {
      await connectOwnerEntry(requestedRoom, requestedTicket);
    } else if (/^[A-Z0-9_-]{4,32}$/.test(requestedRoom)) {
      hideConnectionLanding();
      demoMode = false;
      ui.connectionMode.value = 'official';
      syncConnectionModeUi();
      ui.roomId.value = requestedRoom;
      setStatus('wait', '请输入房间密码', 'ROOM');
      ui.frameState.textContent = `房间 ${requestedRoom} · 等待密码验证`;
      ui.roomPassword.focus();
    } else showConnectionLanding();
  })
  .catch((error) => {
    setStatus('error', '地图加载失败', 'ERROR');
    ui.frameState.textContent = error.message;
    console.error(error);
  });
requestAnimationFrame(animate);
